import {
  formatDurationHoursMinutesSlot,
  minuteOffsetToTime,
} from '@/components/events/helpers/minute-offset-to-time.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { getEndFlowPlaylistMedia, getStartFlowPlaylistMedia } from '@/helpers/flow-media-playlist.helper';
import {
  LockClockSharp,
  PauseCircleOutlineTwoTone,
  EditOutlined,
  People,
  PeopleOutline,
  VideoLibrary,
  LibraryAddCheckOutlined,
  DeleteOutlined,
  RemoveCircleOutline,
} from '@mui/icons-material';
import {
  Card,
  Divider,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Box,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  MenuItem,
  Select,
  type SvgIconProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import {
  useEffect,
  useState,
  useRef,
  cloneElement,
  isValidElement,
  useMemo,
  useCallback,
  type ReactElement,
} from 'react';
import InlineSlotTimingForm, { type InlineSlotTimingFormHandle } from './slot-timing/inline-slot-timing-form';
import { isMuiPortalDismissExemptTarget } from './is-mui-portal-dismiss-exempt';
import InlineSlotEditForm from './slot-edit/inline-slot-edit-form';
import { slotInlineOutlinedActionSx } from './slot-inline-action-buttons';
import { formatSessionSegmentIndexLabel, shouldOfferSlotRemoval } from './session-segment.helpers';
import type { ScheduleInlineDetailsTab } from '../edit-details/schedule-inline-details-tab';
import { isFlowStartRowSlotAssetsPresentation } from '../flow-detail.types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ItemIcons from './item-icons';
import { SlotRightSectionPlayIcon, type SlotSegmentAnchor } from './slot-right-section-play-icon';
import { FlowControlPlayColumnRail } from './flow-control-play-column-rail';
import { useFlowControlSegmentActive } from './use-flow-control-segment-active';
import { useFlowControlSegmentStatus } from './use-flow-control-segment-status';
import { useFlowControlNextCueSegmentExpandId } from './use-flow-control-next-cue-segment-expand-id';
import {
  flowControlActiveSegmentBackground,
  flowControlNextCueSegmentBackground,
  resolveFlowControlSegmentAccent,
} from '@/helpers/flow-control-segment-accent.helper';
import { isFlowControlNextCueSegmentRow } from '@/helpers/flow-control-playing-segment.helper';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import SlotListLeftLeading from './slot-list-left-leading';
import { getSlotListColumnLayout } from './slot-list-layout';
import { SlotListExpandedLeftColumn, slotListExpandedRightColumnSx } from './slot-list-expanded-row-columns';
import Actions from './actions';
import { useTranslation } from 'react-i18next';
import { useSlotTimingPreview } from './slot-timing-preview-store';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { toFlowUiMode } from '@/components/flows/detail/normalize-flow-plan-view';
import { useModeStore } from '@/state/stores/use-mode-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';
import type { Mode } from '@/types/entity-mode';
import { useSearchParams } from 'react-router';
import sessionTypeToIcon from '@/components/events/detail/tabs/sessions/helpers/session-type-to-icon.helper';
import TimePicker from '@/components/common/time-picker';
import { parse, format, addDays } from 'date-fns';
import { SessionPublish } from '@/components/events/detail/tabs/sessions/sessions-grid/session-actions';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import { ServerError } from '@/types/errors/server.error';
import type { EventDto, EventSlotDto } from '@/types/dtos/events/response/event.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { useTranslations } from '@/i18n/use-translations';
import {
  isEmbeddedDraftLayerSession,
  pickNewerSession,
  resolveSessionOriginIdFromFlowSlot,
} from '@/components/flows/detail/flow-detail.utils';
import {
  getFlowContributors,
  getContributorFlowRoles,
  getFlowModeratorStatusIconColor,
  getSessionContributors,
  getContributorSessionRoles,
} from '@/utils/contributor-extraction.utils';
import { resolveMediaListIndicatorColor } from '@/helpers/media-item-aggregate-status.helper';

type StaticItem = {
  title?: string | ReactNode;
  children?: ReactNode;
  time: string;
  duration?: number;
};

function getEventSlotStartItemDayLines(
  event: EventDto,
  flowToUse: FlowDto,
  /** URL `eventSlotId` (plan slot id): prefer `event.slots` entry with `origin ===` this for ORGANIZE. */
  urlEventSlotId?: string | null,
): { multiDay: boolean; dayNumber: number; dateStr: string } | null {
  if (!event.days?.length) return null;
  let slotFromEvent: EventSlotDto | undefined;
  if (urlEventSlotId && event.slots?.length) {
    slotFromEvent =
      event.slots.find((s) => s.origin === urlEventSlotId) ?? event.slots.find((s) => s.id === urlEventSlotId);
  }
  const selectedDayId = slotFromEvent?.day?.id ?? flowToUse.slot?.day?.id ?? event.days[0]?.id ?? '';
  const dayIndex = event.days.findIndex((d) => d.id === selectedDayId);
  if (dayIndex < 0 || !event.startDate) return null;
  return {
    multiDay: event.days.length > 1,
    dayNumber: dayIndex + 1,
    dateStr: format(addDays(event.startDate, dayIndex), 'dd/MM'),
  };
}

type ItemProps = {
  slot?: FlowSlotDto;
  staticItem?: StaticItem;
  sortableId?: string;
  isDropTarget?: boolean;
  /** `userInitiated === false` selects the slot + URL without opening the small-screen details overlay */
  onClick?: (userInitiated?: boolean) => void;
  refreshing?: boolean;
  variant?: 'normal' | 'ghost';
  locked?: boolean;
  selected?: boolean;
  shortenedBy?: number;
  offsetViolation?: boolean;
  setEditingTiming?: (editing: boolean) => void;
  startEndItem?: boolean;
  gapAfter?: number;
  onSlotUpdate?: (updatedSlot: FlowSlotDto) => void;
  onPreviewSlotUpdate?: (updatedSlot: FlowSlotDto) => void;
  onTimingSectionClick?: () => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  cancelChanges?: () => void;
  hydrated?: boolean;
  isEditingTiming?: boolean;
  isEditingEdit?: boolean;
  onSlotDeleted?: () => void;
  localSlots?: FlowSlotDto[];
  previewFlow?: FlowDto;
  flowEndOffset?: number;
  onStartTimeChange?: (startTime: string) => void;
  onPreviewStartTimeChange?: (startTime: string) => void;
  showStartItemFooter?: boolean;
  isStartItem?: boolean;
  expandedSlotId?: string | null;
  onExpandedChange?: (slotId: string | null) => void;
  isSmallScreen?: boolean;
  expandedFormType?: 'timing' | 'new' | null;
  onFormTypeChange?: (slotId: string, formType: 'timing' | 'new' | null) => void;
  onShowForm?: (
    gapId: string,
    offset: number,
    slotType?: 'Session' | 'Break',
    insertBeforeSlotId?: string,
    insertAfterSlotId?: string,
  ) => void;
  event?: EventDto;
  flow?: FlowDto;
  /** When set (from SlotList), overrides store `flow.slot` for time math — required when `modeOverride` is ORGANIZE and global `flow` is still PLAN. */
  listFlowStartOffset?: number;
  onDayChange?: (dayId: string) => void;
  onPreviewDayChange?: (dayId: string) => void;
  onShowFlowDetails?: (mode: import('../flow-detail.types').FlowDetailMode) => void;
  setFlowDetailsDirection?: (direction: 'up' | 'down') => void;
  flowDuration?: number;
  isUntimed?: boolean;
  placeholderHeight?: number;
  isPlaceholder?: boolean;
  isHiddenDropTarget?: boolean;
  timePaddingX?: number;
  hideTime?: boolean;
  hideDuration?: boolean;
  containerId?: string;
  disableDrop?: boolean;
  /** When set, overrides useFlowMode for display (e.g. ORGANIZE for assets tab) */
  modeOverride?: Mode;
  gapBefore?: { startOffset: number; duration: number };
  gapBeforeInsertAfterSlotId?: string;
  forceGapBefore?: boolean;
  hideGapTiming?: boolean;
  hideGapStartTime?: boolean;
  /** When true, gap row omits NEW/BREAK (read-only slot assets / control columns). */
  hideGapSlotWizardAndBreak?: boolean;
  customContent?: ReactNode;
  setSelectedSlot?: (slot?: FlowSlotDto) => void;
  forceTimedPreview?: boolean;
  /** When true, right section shows only publish icon (flow schedule tab). When false, shows contributor/media icons (flow slot assets tab) */
  rightSectionOnlyPublishIcon?: boolean;
  /** When true, clicking middle section on small screens opens overlay (e.g. Slot Assets tab) */
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  /** Narrow Slot Assets: toggle inline SlotAssetsDetails below the row (with overlay) */
  onSlotAssetsInlineDetailsToggle?: (slotId: string) => void;
  /** Clear inline details for this slot when collapsing the row from middle (keeps toggle in sync) */
  onSlotAssetsInlineDetailsClose?: (slotId: string) => void;
  /** True when narrow inline details are open for this slot (enables outside-click to close) */
  slotInlineDetailsOpen?: boolean;
  /** Start-row media stack / video icon: open this mode instead of flowSettings (e.g. flowControl on Control tab) */
  startRowMediaOpenDetailsMode?: import('../flow-detail.types').FlowDetailMode;
  /** Flow schedule tab: after session publish succeeds, keep schedule tab / URL in sync */
  onSessionPublishSuccess?: () => void;
  /** Narrow Control tab: expand PerformMiddleSection inline */
  flowControlInlinePerform?: boolean;
  flowControlPerformExpandedKey?: string | null;
  onFlowControlPerformExpand?: (key: string | null) => void;
  onFlowControlStartItemMiddleClick?: () => void;
  /** Narrow slot assets: flow start row toggles inline moderators/media (see list.tsx) */
  onFlowStartSlotAssetsInlinePress?: () => void;
  onFlowEndSlotAssetsInlinePress?: () => void;
  /** Synthetic data-slot-id for start row when showing flow-level slot-assets inline panel */
  startItemInlineSlotAssetsRowId?: string;
  endItemInlineSlotAssetsRowId?: string;
  /** Control tab: toggle inline draft media under this row */
  flowControlInlineMedia?: boolean;
  onFlowControlInlineMediaToggle?: (slotId: string) => void;
  flowControlInlineMediaOpen?: boolean;
  /** Control tab: inert play icon in the right column */
  rightSectionPlayIcon?: boolean;
  /** Schedule tab: end-of-flow row has no slot media — hide VideoLibrary in the right column */
  hideEndItemMediaIndicator?: boolean;
  /** Schedule tab: left-column drag handle; right column narrows by the same width */
  showSlotDragHandle?: boolean;
  /** Schedule tab while live: slot is at/before the playing segment and cannot be reordered */
  scheduleLiveLockBlocked?: boolean;
  /** Narrow schedule inline: which details widget is shown under the row */
  smallScreenInlineDetailsVariant?: 'slotAssets' | 'schedule';
  inlineScheduleDetailsTab?: ScheduleInlineDetailsTab;
  onInlineScheduleDetailsTabChange?: (tab: ScheduleInlineDetailsTab) => void;
};

type SlotTimeTextProps = {
  slot?: FlowSlotDto;
  flowStartOffset: number;
  staticTime?: string;
  isUntimed: boolean;
  hideTime?: boolean;
  forceTimedPreview?: boolean;
};

const SlotTimeText = ({
  slot,
  flowStartOffset,
  staticTime,
  isUntimed,
  hideTime,
  forceTimedPreview,
}: SlotTimeTextProps) => {
  const preview = useSlotTimingPreview(slot?.id);
  if (hideTime) return null;
  if (isUntimed && !forceTimedPreview) return null;
  const offsetMinutes = preview?.offsetMinutes ?? slot?.computedOffsetMinutes;
  if (offsetMinutes === undefined && !staticTime) return null;
  const time = offsetMinutes !== undefined ? minuteOffsetToTime(flowStartOffset + offsetMinutes) : (staticTime ?? '');
  return <>{time}</>;
};

/** Mirrors `SlotTimeText` visibility so we do not reserve layout (e.g. minHeight) when time is not shown. */
function useSlotTimeVisible({
  slot,
  staticTime,
  isUntimed,
  hideTime,
  forceTimedPreview,
}: Omit<SlotTimeTextProps, 'flowStartOffset'>): boolean {
  const preview = useSlotTimingPreview(slot?.id);
  if (hideTime) return false;
  if (isUntimed && !forceTimedPreview) return false;
  const offsetMinutes = preview?.offsetMinutes ?? slot?.computedOffsetMinutes;
  if (offsetMinutes === undefined && !staticTime) return false;
  return true;
}

type SlotDurationDisplayProps = {
  slot?: FlowSlotDto;
  staticDuration?: number;
  isUntimed: boolean;
  hideDuration?: boolean;
  forceTimedPreview?: boolean;
  /** Merged session (store + API) — flow slot copy can lag behind session PATCH / websocket refetch. */
  sessionPlannedDurationMinutes?: number;
};

const SlotDurationDisplay = ({
  slot,
  staticDuration,
  isUntimed,
  hideDuration,
  forceTimedPreview,
  sessionPlannedDurationMinutes,
}: SlotDurationDisplayProps) => {
  const preview = useSlotTimingPreview(slot?.id);
  if (hideDuration) return null;
  if (slot) {
    if (isUntimed && !forceTimedPreview) {
      // Authority: session API stores plannedDurationMinutes; flow slot mirrors it for the schedule row.
      // Prefer flow slot (top-level + embedded content) before merged session so optimistic localSlots updates
      // win over stale session store before refetch; merged session helps other devices / post-sync.
      const plannedDuration =
        preview?.durationMinutes ??
        slot.plannedDurationMinutes ??
        (typeof slot.content === 'object' && slot.content && 'plannedDurationMinutes' in slot.content
          ? slot.content.plannedDurationMinutes
          : undefined) ??
        sessionPlannedDurationMinutes ??
        0;
      return (
        <Stack direction="column" alignItems="center" spacing={0} sx={{ minHeight: 0, width: '100%' }}>
          <Typography
            variant="slotDuration"
            color="text.secondary"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {`${plannedDuration} min`}
          </Typography>
        </Stack>
      );
    }

    const computedDuration = preview?.durationMinutes ?? slot.computedDurationMinutes ?? 0;
    const plannedDuration = slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? 0;
    const shortenedBy = plannedDuration > computedDuration ? plannedDuration - computedDuration : 0;
    const isGapSlot = slot.contentType === 'Gap';
    const hasTwoLineDuration = shortenedBy > 0 && slot?.contentType !== 'Break' && !isGapSlot;
    const fullDurationWhenShortened = shortenedBy > 0 && !isGapSlot ? `/${plannedDuration}` : '';
    const duration = `${computedDuration}${fullDurationWhenShortened}${hasTwoLineDuration ? '' : ' min'}`;

    if (!duration) return null;

    return (
      <Stack direction="column" alignItems="center" spacing={0}>
        <Typography
          variant="slotDuration"
          color={computedDuration === 0 ? 'warning' : 'text.secondary'}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {duration}
        </Typography>
        {hasTwoLineDuration && (
          <Typography
            variant="slotDuration"
            color="text.secondary"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            min
          </Typography>
        )}
      </Stack>
    );
  }

  if (staticDuration === undefined) return null;

  return (
    <Stack direction="column" alignItems="center" spacing={0}>
      <Typography
        variant="slotDuration"
        color="text.secondary"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
      >
        {`${staticDuration} min`}
      </Typography>
    </Stack>
  );
};

export default function Item({
  slot,
  staticItem,
  sortableId,
  isDropTarget = false,
  onClick,
  refreshing,
  variant = 'normal',
  locked = false,
  selected = false,
  shortenedBy = 0,
  offsetViolation = false,
  startEndItem = false,
  gapAfter = 0,
  onSlotUpdate,
  onPreviewSlotUpdate,
  onTimingSectionClick,
  onShowTimingAlert,
  onShowPublishAlert,
  cancelChanges,
  setEditingTiming,
  isEditingTiming = false,
  isEditingEdit = false,
  onSlotDeleted,
  localSlots,
  previewFlow,
  flowEndOffset,
  onStartTimeChange,
  onPreviewStartTimeChange,
  showStartItemFooter = false,
  isStartItem = false,
  expandedSlotId,
  onExpandedChange,
  isSmallScreen = false,
  expandedFormType = null,
  onFormTypeChange,
  onShowForm,
  event,
  flow,
  listFlowStartOffset,
  onDayChange,
  onPreviewDayChange,
  onShowFlowDetails,
  setFlowDetailsDirection,
  flowDuration,
  isUntimed = false,
  placeholderHeight,
  isPlaceholder = false,
  isHiddenDropTarget = false,
  timePaddingX,
  hideTime = false,
  hideDuration = false,
  containerId,
  disableDrop = false,
  modeOverride,
  gapBefore,
  gapBeforeInsertAfterSlotId,
  forceGapBefore = false,
  hideGapTiming = false,
  hideGapStartTime = false,
  hideGapSlotWizardAndBreak = false,
  customContent,
  setSelectedSlot,
  forceTimedPreview = false,
  rightSectionOnlyPublishIcon = false,
  middleSectionOpensOverlayOnSmallScreen = false,
  onSlotAssetsInlineDetailsToggle,
  onSlotAssetsInlineDetailsClose,
  slotInlineDetailsOpen = false,
  startRowMediaOpenDetailsMode,
  onSessionPublishSuccess,
  flowControlInlinePerform = false,
  flowControlPerformExpandedKey = null,
  onFlowControlPerformExpand,
  onFlowControlStartItemMiddleClick,
  onFlowStartSlotAssetsInlinePress,
  onFlowEndSlotAssetsInlinePress,
  startItemInlineSlotAssetsRowId,
  endItemInlineSlotAssetsRowId,
  flowControlInlineMedia = false,
  onFlowControlInlineMediaToggle,
  flowControlInlineMediaOpen = false,
  rightSectionPlayIcon = false,
  hideEndItemMediaIndicator = false,
  showSlotDragHandle = false,
  scheduleLiveLockBlocked = false,
  smallScreenInlineDetailsVariant,
  inlineScheduleDetailsTab,
  onInlineScheduleDetailsTabChange,
}: ItemProps) {
  const startRowMediaDetailsMode = startRowMediaOpenDetailsMode ?? 'flowSettings';
  const { t } = useTranslation();
  const t2 = useTranslations();
  const theme = useTheme();
  const allContributors = useEventStore((s) => s.contributors);
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');

  // Helper functions to get contributor and media colors for flow
  const getFlowContributorColor = (flow?: FlowDto): SvgIconProps['color'] => {
    if (!flow || !allContributors) return 'disabled';
    const flowContributors = getFlowContributors(flow, allContributors);
    if (flowContributors.length === 0) return 'disabled';

    const flowId = typeof flow.origin === 'string' ? flow.origin : flow.id;
    let totalRoles = 0;
    let totalInvited = 0;
    let totalConfirmed = 0;

    flowContributors.forEach((contributor) => {
      const flowRoles = getContributorFlowRoles(contributor, flowId);
      flowRoles.forEach((role) => {
        totalRoles++;
        if (role.invitedAt) totalInvited++;
        if (role.acceptedAt) totalConfirmed++;
      });
    });

    // Also check slot-level contributors
    flow.slots?.forEach((slot) => {
      if (slot.contentType === 'Session' && slot.content) {
        const sessionContributors = getSessionContributors(slot.content, allContributors);
        const sessionId = slot.content.id;
        sessionContributors.forEach((contributor) => {
          const sessionRoles = getContributorSessionRoles(contributor, sessionId);
          sessionRoles.forEach((role) => {
            totalRoles++;
            if (role.invitedAt) totalInvited++;
            if (role.acceptedAt) totalConfirmed++;
          });
        });
      }
    });

    if (totalRoles === 0) return 'disabled';
    if (totalInvited < totalRoles) return 'primary';
    if (totalInvited === totalConfirmed && totalConfirmed > 0) return 'success';
    if (totalInvited > 0) return 'info';
    return 'action';
  };

  const getFlowMediaColorForList = (flowMediaList: FlowDto['media']): SvgIconProps['color'] =>
    resolveMediaListIndicatorColor(flowMediaList);

  const getFlowMediaColor = (flow?: FlowDto) => getFlowMediaColorForList(flow ? getStartFlowPlaylistMedia(flow) : []);
  const getEndFlowMediaColor = (flow?: FlowDto) => getFlowMediaColorForList(flow ? getEndFlowPlaylistMedia(flow) : []);

  const [isEditingStartTime, setIsEditingStartTime] = useState(false);
  const [pendingStartTime, setPendingStartTime] = useState<string | null>(null);
  const initialStartTimeRef = useRef<string | null>(null);
  const didAcceptStartTimeRef = useRef(false);
  const inlineTimingFormRef = useRef<InlineSlotTimingFormHandle | null>(null);
  const flowControlSlotSegmentKind =
    slot?.contentType === 'Break' ? 'break' : slot?.contentType === 'Session' ? 'session' : undefined;
  const isFlowControlSlotPlaying = useFlowControlSegmentActive(
    'slot',
    flowControlInlineMedia ? flowControlSlotSegmentKind : undefined,
    slot?.id,
  );
  const isExpanded = expandedSlotId === slot?.id;
  /** Narrow: list chrome only when the row is meaningfully open (expanded, inline panel, or control perform). Wide: follow `selected`. */
  const smallScreenShowsSelectedChrome =
    isExpanded ||
    slotInlineDetailsOpen ||
    flowControlInlineMediaOpen ||
    isFlowControlSlotPlaying ||
    (Boolean(flowControlInlinePerform) &&
      flowControlPerformExpandedKey != null &&
      slot?.id != null &&
      flowControlPerformExpandedKey === slot.id);
  const showSelectedChrome = Boolean(selected && (!slot || (isSmallScreen ? smallScreenShowsSelectedChrome : true)));
  const isStartItemFooter = startEndItem && (onStartTimeChange || showStartItemFooter);
  const flowStore = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const flowMode = useModeStore((s) => s.flowMode);

  // Get current flow ID for mode switching
  const flowId = useMemo(() => {
    const activeFlow = flow ?? flowStore;
    if (activeFlow?.origin) {
      return typeof activeFlow.origin === 'string' ? activeFlow.origin : activeFlow.origin.id;
    }
    return activeFlow?.id;
  }, [flow, flowStore]);

  // Get current mode: modeOverride takes precedence (e.g. ORGANIZE for assets tab), then flowMode store
  const currentMode: Mode = useMemo(() => {
    if (modeOverride) {
      if (modeOverride === 'PLAN' && flowPlan) return 'PLAN';
      if ((modeOverride === 'ORGANIZE' || modeOverride === 'PERFORM') && flowOrganize) return modeOverride;
    }
    if (flowId && flowMode[flowId]) {
      const requested = flowMode[flowId];
      if (requested === 'PLAN' && flowPlan) return requested;
      if ((requested === 'ORGANIZE' || requested === 'PERFORM') && flowOrganize) return requested;
    }
    // Default: PLAN if available, else ORGANIZE
    if (flowPlan) {
      return 'PLAN';
    }
    if (flowOrganize) {
      return 'ORGANIZE';
    }
    return toFlowUiMode(flowStore?.mode);
  }, [modeOverride, flowId, flowMode, flowPlan, flowOrganize, flowStore]);

  const mode = currentMode;
  const isInlineTimingSectionOpen = Boolean(isExpanded && expandedFormType === 'timing' && isEditingTiming && slot);
  const showFixedStartTimeEditNextToSlotTime =
    isInlineTimingSectionOpen && !startEndItem && slot?.plannedOffsetMinutes !== undefined && !isOrganizeMode(mode);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const sessionOriginId = useMemo(
    () =>
      resolveSessionOriginIdFromFlowSlot(slot, {
        eventSessionsPlan,
        eventSessionsOrganize,
        flowPlan,
      }),
    [slot, eventSessionsPlan, eventSessionsOrganize, flowPlan],
  );

  // Get session data from store based on the flow mode

  const sessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;

    const findSession = (sessions?: typeof eventSessionsPlan) =>
      sessions?.find((s) => {
        const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
        return sOriginId === sessionOriginId;
      });

    const planSession = findSession(eventSessionsPlan);
    const organizeSession = findSession(eventSessionsOrganize);

    if (currentMode === 'PLAN') {
      return planSession ?? organizeSession;
    }

    if (currentMode === 'ORGANIZE' || currentMode === 'PERFORM') {
      return organizeSession;
    }

    return planSession ?? organizeSession;
  }, [sessionOriginId, eventSessionsPlan, eventSessionsOrganize, currentMode]);

  const planSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsPlan?.find((s) => {
      const planOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return planOriginId === sessionOriginId || s.id === sessionOriginId;
    });
  }, [eventSessionsPlan, sessionOriginId]);

  // Case 3: Fetch from backend to keep session details fresh (media updates don't always flow through list queries).
  // Only fetch for the selected slot or when the event session store has no copy yet — list data covers other rows.
  const selectedSlotId = useFlowStore((s) => s.selectedSlot?.id);
  const isSelectedSlot = selectedSlotId === slot?.id;
  const shouldFetchFromBackend = !!sessionOriginId && (isSelectedSlot || !sessionFromStore);
  const backendMode =
    currentMode === 'PLAN' ? 'PLAN' : currentMode === 'ORGANIZE' || currentMode === 'PERFORM' ? 'ORGANIZE' : undefined;
  const { data: sessionFromBackend, error: sessionFromBackendError } = useGetSessionQuery(
    shouldFetchFromBackend ? sessionOriginId : undefined,
    backendMode,
  );
  const planNotFound =
    currentMode === 'PLAN' &&
    sessionFromBackendError instanceof ServerError &&
    sessionFromBackendError.statusCode === 404;
  const organizeSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsOrganize?.find((s) => {
      const originId = s.origin ? getObjectId(s.origin) : s.id;
      return originId === sessionOriginId || s.id === sessionOriginId;
    });
  }, [eventSessionsOrganize, sessionOriginId]);
  const shouldFetchOrganizeFallback = shouldFetchFromBackend && planNotFound && !!organizeSessionFromStore;
  const { data: sessionFromBackendOrganize } = useGetSessionQuery(
    shouldFetchOrganizeFallback ? sessionOriginId : undefined,
    'ORGANIZE',
  );
  /** When not in PLAN view, still fetch PLAN for publish state (matches `UpdateSessionSlot`). In PLAN view, `sessionFromBackend` is already PLAN. */
  const { data: sessionFromBackendPlanWhenOrganizeView } = useGetSessionQuery(
    shouldFetchFromBackend && backendMode !== 'PLAN' ? sessionOriginId : undefined,
    'PLAN',
  );
  const sessionFromBackendPlan = backendMode === 'PLAN' ? sessionFromBackend : sessionFromBackendPlanWhenOrganizeView;
  /** 404 on the mode-scoped session GET — treat as deleted only if the event session store has no copy yet (avoids stale 404 after publish while refetch lags behind sessions list). */
  const isDeletedSession =
    sessionFromBackendError instanceof ServerError &&
    sessionFromBackendError.statusCode === 404 &&
    !planNotFound &&
    !sessionFromStore;

  const slotSession = useMemo(() => {
    if (slot?.contentType !== 'Session' || !slot.content || typeof slot.content !== 'object') {
      return undefined;
    }
    const embedded = slot.content;
    const allowOrganizeFallback = currentMode === 'PLAN' && planNotFound;
    if (
      embedded.mode === backendMode ||
      (allowOrganizeFallback && embedded.mode === 'ORGANIZE') ||
      (currentMode === 'PLAN' && isEmbeddedDraftLayerSession(embedded.mode))
    ) {
      return embedded;
    }
    return undefined;
  }, [slot, backendMode, currentMode, planNotFound]);

  // Determine final session content: newest(store vs backend) > slot.content
  const sessionContent = useMemo(() => {
    if (slot?.contentType !== 'Session') {
      return slot?.content;
    }

    if (isDeletedSession) {
      return undefined;
    }

    const backendSession = sessionFromBackend || sessionFromBackendOrganize;
    return pickNewerSession(sessionFromStore, backendSession) || slotSession;
  }, [slot, isDeletedSession, sessionFromStore, sessionFromBackend, sessionFromBackendOrganize, slotSession]);

  const isUnpublishedSession =
    slot?.contentType === 'Session' && !isDeletedSession && (!sessionContent || !sessionContent?.title);

  /** Slot Assets tab: block only deleted sessions; unpublished sessions still open asset widgets (PLAN fallback). */
  const blockSlotAssetsInlineInteractions =
    smallScreenInlineDetailsVariant === 'slotAssets' && slot?.contentType === 'Session' && isDeletedSession;

  /** Untimed duration label: flow slot can lag session PATCH / websocket; prefer merged session like title/publish. */
  const sessionPlannedDurationMinutesForDisplay = useMemo(() => {
    if (slot?.contentType !== 'Session' || !sessionContent || typeof sessionContent !== 'object') {
      return undefined;
    }
    return (sessionContent as SessionDto).plannedDurationMinutes;
  }, [slot?.contentType, sessionContent]);

  /** Match `UpdateSessionSlot` / Basics: store + PLAN API, not slot-embedded session alone. */
  const sessionPlanForPublish = useMemo(
    () => pickNewerSession(planSessionFromStore, sessionFromBackendPlan),
    [planSessionFromStore, sessionFromBackendPlan],
  );

  /**
   * SessionPublish uses PLAN from `session` when mode is PLAN. Prefer merged plan (store + backend)
   * over slot copy when the slot is not strictly newer — avoids flicker after flow sync overwrites slot.
   */
  const sessionForPublishButton = useMemo(() => {
    if (!sessionContent || typeof sessionContent !== 'object') return undefined;
    const asDto = sessionContent as SessionDto;
    if (asDto.mode === 'PLAN' && sessionPlanForPublish) {
      return asDto.version > sessionPlanForPublish.version ? asDto : sessionPlanForPublish;
    }
    return asDto;
  }, [sessionContent, sessionPlanForPublish]);

  const hasPlanDataForPublish = !!flowPlan;
  const isGhostItem = variant === 'ghost';
  const gapPreview = useSlotTimingPreview(slot?.id);
  const isGapSlot = slot?.contentType === 'Gap';
  const isFixedSlot = slot?.plannedOffsetMinutes !== undefined;
  const gapDurationMinutes = gapPreview?.durationMinutes ?? slot?.computedDurationMinutes;
  const isHiddenGapSlot = isGapSlot && typeof gapDurationMinutes === 'number' && gapDurationMinutes <= 0;
  const isStaticNonDroppable = staticItem !== undefined && !isGhostItem && !isDropTarget;
  const reorderLeadingBlocked =
    isOrganizeMode(mode) ||
    isGapSlot ||
    isFixedSlot ||
    scheduleLiveLockBlocked ||
    (isGhostItem && !isDropTarget && !isHiddenDropTarget) ||
    isStaticNonDroppable;
  /** Suspend reorder while list DnD is blocked (read-only). Inline timing stays draggable via long-press. */
  const slotReorderSuspended = disableDrop;
  const shouldDisableDrag = reorderLeadingBlocked || slotReorderSuspended;
  const shouldDisableDrop = isStaticNonDroppable || isGapSlot || disableDrop || scheduleLiveLockBlocked;
  /** Keep handle visible; disable interaction while reorder is suspended. */
  const dragHandleInteractionDisabled = reorderLeadingBlocked || slotReorderSuspended;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    // Allow only explicit drop targets; visual ghost items are not droppable.
    disabled:
      shouldDisableDrag || shouldDisableDrop
        ? { draggable: shouldDisableDrag, droppable: shouldDisableDrop }
        : undefined,
    id:
      sortableId ??
      slot?.id ??
      (isGhostItem ? `ghost-${staticItem?.time}` : `static-${staticItem?.title ?? staticItem?.time}`),
    data: containerId ? { containerId } : undefined,
  });
  /** PR-552 handle column changes row geometry; keep pre-drag layout on the sortable node while dragging. */
  const dragHandleColumnInLayout = showSlotDragHandle && !isDragging;
  const slotListColumnLayout = useMemo(
    () => getSlotListColumnLayout(dragHandleColumnInLayout),
    [dragHandleColumnInLayout],
  );
  const { leftMinWidthPx, rightWidthPx } = slotListColumnLayout;
  const leftColumnMinWidth = `${leftMinWidthPx}px`;
  const rightColumnWidth = `${rightWidthPx}px`;
  const rightColumnBoxSx = useMemo(
    () => ({ minWidth: rightColumnWidth, width: rightColumnWidth, flexShrink: 0 as const }),
    [rightColumnWidth],
  );
  const rightSectionStackSx = useMemo(
    () => ({
      ...rightColumnBoxSx,
      height: '100%',
      alignSelf: 'stretch' as const,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      touchAction: 'manipulation' as const,
    }),
    [rightColumnBoxSx],
  );
  const flowControlRightSectionStackSx = useMemo(
    () => ({
      height: '100%',
      alignSelf: 'stretch' as const,
      display: 'flex',
      touchAction: 'manipulation' as const,
    }),
    [],
  );
  const showRowDragHandle =
    dragHandleColumnInLayout && Boolean(slot) && !startEndItem && !isGapSlot && !reorderLeadingBlocked;
  const showRowLockInLeading =
    dragHandleColumnInLayout && locked && (!startEndItem || !onStartTimeChange) && !showRowDragHandle;
  const hasExpandedActionRow = Boolean(isExpanded && variant === 'normal' && !startEndItem && slot && !isDragging);
  /** Avoid timed/untimed preview toggling row height on the measured sortable node mid-drag. */
  const layoutForceTimedPreview = isDragging ? false : forceTimedPreview;
  const flowFromStore = useFlowStore((s) => s.flow);
  // IMPORTANT: Always use flow from store when available (has correct flow.slot set based on eventSlotIdParam)
  // The store is the SINGLE SOURCE OF TRUTH for the eventSlot context
  // Only fall back to flow prop if store doesn't have flow yet
  // This ensures publish button uses the correct eventSlot context
  // Even if flowFromStore.slot.id === flow.slot.id, prefer flowFromStore because it has the latest version/publishedModes
  const flowToUse = startEndItem && isStartItem ? flow : (flowFromStore ?? flow);
  const offerSlotRemoval = slot ? shouldOfferSlotRemoval(slot, flowToUse) : false;
  const eventSlotStartItemDayLines = useMemo(() => {
    if (!startEndItem || !isStartItem || !eventSlotIdParam || !event?.days?.length || !flowToUse) return null;
    return getEventSlotStartItemDayLines(event, flowToUse, eventSlotIdParam);
  }, [startEndItem, isStartItem, eventSlotIdParam, event, flowToUse]);

  const showEventSlotFlowStartTitleRow =
    Boolean(startEndItem && isStartItem && eventSlotIdParam && flowToUse) &&
    (isOrganizeMode(mode) || isFlowStartRowSlotAssetsPresentation(startRowMediaOpenDetailsMode));

  const showEventSlotFlowStartDayReadOnly =
    isOrganizeMode(mode) || isFlowStartRowSlotAssetsPresentation(startRowMediaOpenDetailsMode);

  /** Event-slot flow start row — same two-line pattern as session title + actors */
  const flowStartRowModeratorNames = useMemo(() => {
    if (!showEventSlotFlowStartTitleRow || !flowToUse) return '';
    return getFlowContributors(flowToUse, allContributors)
      .map((c) => c.profiles?.[0]?.displayName)
      .filter(Boolean)
      .join(', ');
  }, [showEventSlotFlowStartTitleRow, flowToUse, allContributors]);

  const originalHeightRef = useRef<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const flowForTiming = previewFlow ?? flow ?? flowFromStore;
  /** Match SlotList `flowToUse` so segment index/total use the same flow document as the rendered slots. */
  const flowForSegmentLookup = useMemo(() => {
    if (previewFlow) return previewFlow;
    if (mode === 'ORGANIZE' && flowOrganize) return flowOrganize;
    if (mode === 'PLAN' && flowPlan) return flowPlan;
    return flow ?? flowFromStore;
  }, [previewFlow, mode, flowOrganize, flowPlan, flow, flowFromStore]);
  const sessionSegmentLabel = useMemo(() => {
    if (!slot) return null;
    return formatSessionSegmentIndexLabel(flowForSegmentLookup, slot);
  }, [slot, flowForSegmentLookup]);
  const derivedFlowStartOffset = eventSlotIdParam ? (flowForTiming?.slot?.startOffsetMinutes ?? 0) : 0;
  const flowStartOffset = listFlowStartOffset ?? derivedFlowStartOffset;
  const hasGapBefore =
    !!gapBefore &&
    Number.isFinite(gapBefore.startOffset) &&
    !isUntimed &&
    (forceGapBefore || (!startEndItem && gapBefore.duration > 0 && slot?.plannedOffsetMinutes !== undefined));
  const gapTime = hasGapBefore ? minuteOffsetToTime(flowStartOffset + gapBefore.startOffset) : '';
  const shouldShowStartTime = !isStartItem || !!eventSlotIdParam;

  const baseHeight = isStartItemFooter
    ? '40px'
    : startEndItem && !isStartItem && flowDuration !== undefined && flowDuration > 0
      ? '40px'
      : startEndItem
        ? '32px'
        : isExpanded && variant === 'normal' && !startEndItem && slot
          ? slot?.contentType === 'Session'
            ? '80px'
            : '68px'
          : variant === 'normal'
            ? slot?.contentType === 'Session'
              ? '48px'
              : '32px'
            : '32px';
  const gapPadding = theme.spacing(1);
  const heightWithGap = hasGapBefore ? `calc(${baseHeight} + 32px + ${gapPadding})` : baseHeight;

  // Store original height when drag starts (use base height, not expanded)
  useEffect(() => {
    if (isDragging && !originalHeightRef.current) {
      originalHeightRef.current = heightWithGap;
    } else if (!isDragging) {
      originalHeightRef.current = null;
    }
  }, [isDragging, baseHeight, hasGapBefore]);

  const inlineSlotIdForAssets = slot?.id ?? startItemInlineSlotAssetsRowId ?? endItemInlineSlotAssetsRowId;

  // Outside click: collapse expanded row and/or close narrow inline details (panel renders below the card)
  useEffect(() => {
    if (!slot && !startItemInlineSlotAssetsRowId && !endItemInlineSlotAssetsRowId) return;
    const listenExpanded = Boolean(isExpanded && onExpandedChange);
    const listenInline = Boolean(slotInlineDetailsOpen && onSlotAssetsInlineDetailsClose && inlineSlotIdForAssets);
    if (!listenExpanded && !listenInline) return;

    const handleGlobalClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;

      // Portaled MUI overlays (pickers, Select/Menu, dialogs) are outside the card and
      // [data-slot-inline-details] — don't collapse the row / inline details for those clicks.
      if (isMuiPortalDismissExemptTarget(event)) {
        return;
      }

      // Inline details (schedule slot assets) render below the card, not inside [data-testid="flow-slot"]
      const inlineDetailsRoot = target.closest('[data-slot-inline-details]');
      if (
        inlineDetailsRoot instanceof HTMLElement &&
        inlineSlotIdForAssets &&
        inlineDetailsRoot.getAttribute('data-slot-id') === inlineSlotIdForAssets
      ) {
        return;
      }

      // Interactive elements on *our* card only — clicks on buttons outside the row should still dismiss
      if (cardRef.current?.contains(target)) {
        if (
          target.closest('button') ||
          target.closest('[role="button"]') ||
          target.closest('input') ||
          target.closest('select') ||
          target.closest('a')
        ) {
          return;
        }
      }

      // Find the clicked slot card
      const clickedSlotCard = target.closest('[data-testid="flow-slot"]') as HTMLElement | null;
      const clickedSlotId = clickedSlotCard?.getAttribute('data-slot-id');

      // Only for *this* slot: timing/middle/expanded handle their own clicks — don't dismiss from here
      if (clickedSlotCard && inlineSlotIdForAssets && clickedSlotId === inlineSlotIdForAssets) {
        const cardFirstChild = clickedSlotCard.firstElementChild;
        const timingSection = cardFirstChild?.firstElementChild;
        const isClickingTimingSection = timingSection && (timingSection.contains(target) || timingSection === target);
        const isClickingMiddleSection = target.closest('[data-cursor-element-id="cursor-el-195"]');

        const isClickingExpandedForm =
          target.closest('[data-expanded-form]') ||
          clickedSlotCard.querySelector('[data-expanded-form]')?.contains(target);

        if (isClickingTimingSection || isClickingMiddleSection || isClickingExpandedForm) {
          return;
        }
      }

      // Check if click is inside our own card
      if (cardRef.current && cardRef.current.contains(target)) {
        // If clicking on our own content Stack (middle section), don't collapse (it will toggle)
        if (target.closest('[data-cursor-element-id="cursor-el-195"]')) {
          return;
        }
        // If clicking on the right section (ItemIcons), it has its own handler - don't interfere
        if (target.closest('[data-cursor-element-id="cursor-el-1"]')) {
          return;
        }
        // Otherwise, let the card's onClick handler deal with it
        return;
      }

      const dismissFromOutside = () => {
        if (inlineSlotIdForAssets && slotInlineDetailsOpen) {
          onSlotAssetsInlineDetailsClose?.(inlineSlotIdForAssets);
        }
        if (listenExpanded) {
          onExpandedChange?.(null);
        }
      };

      // Next / Play / transport sit outside the card. Collapsing on this mousedown
      // replaces the button before mouseup, so the click never runs.
      const isOutsidePerformControl = (node: HTMLElement) =>
        !(cardRef.current && cardRef.current.contains(node)) && Boolean(node.closest('button, [role="button"]'));
      if (isOutsidePerformControl(target)) {
        return;
      }

      // Another slot's row: collapsing here in capture runs before that slot's click handlers and
      // shrinks the list — rows below jump up, so the click can miss the intended slot (mobile/desktop).
      // Let the other slot's timing/middle/expanded handlers run; they call handleFormTypeChange and
      // replace expandedSlotId without needing a prior collapse.
      if (clickedSlotCard && clickedSlotId && inlineSlotIdForAssets && clickedSlotId !== inlineSlotIdForAssets) {
        const cardFirstChild = clickedSlotCard.firstElementChild;
        const otherTimingSection = cardFirstChild?.firstElementChild;
        const isClickingOtherTimingSection =
          otherTimingSection && (otherTimingSection.contains(target) || otherTimingSection === target);
        const isClickingOtherMiddleSection = target.closest('[data-cursor-element-id="cursor-el-195"]');
        const isClickingOtherRightSection = target.closest('[data-cursor-element-id="cursor-el-1"]');
        const isClickingOtherExpandedForm =
          target.closest('[data-expanded-form]') ||
          clickedSlotCard.querySelector('[data-expanded-form]')?.contains(target);

        if (
          isClickingOtherTimingSection ||
          isClickingOtherMiddleSection ||
          isClickingOtherRightSection ||
          isClickingOtherExpandedForm
        ) {
          // Inline details are cleared in list's effectiveHandleFormTypeChange with the same
          // batch as expanding the other slot — do not close inline here (capture) or layout races.
          return;
        }

        dismissFromOutside();
        return;
      }

      dismissFromOutside();
    };

    const dismissAfterPerformControl = (event: MouseEvent) => {
      const node = event.target;
      if (!(node instanceof Element) || isMuiPortalDismissExemptTarget(event)) {
        return;
      }
      const inlineDetailsRoot = node.closest('[data-slot-inline-details]');
      if (
        cardRef.current?.contains(node) ||
        (inlineSlotIdForAssets && inlineDetailsRoot?.getAttribute('data-slot-id') === inlineSlotIdForAssets) ||
        !node.closest('button, [role="button"]')
      ) {
        return;
      }
      if (inlineSlotIdForAssets && slotInlineDetailsOpen) {
        onSlotAssetsInlineDetailsClose?.(inlineSlotIdForAssets);
      }
      if (listenExpanded) {
        onExpandedChange?.(null);
      }
    };

    // Use capture phase to catch events before they're stopped.
    // Perform controls dismiss on click, after their own handler has run.
    document.addEventListener('mousedown', handleGlobalClick, true);
    document.addEventListener('touchstart', handleGlobalClick, true);
    document.addEventListener('click', dismissAfterPerformControl);

    return () => {
      document.removeEventListener('mousedown', handleGlobalClick, true);
      document.removeEventListener('touchstart', handleGlobalClick, true);
      document.removeEventListener('click', dismissAfterPerformControl);
    };
  }, [
    isExpanded,
    slot,
    startItemInlineSlotAssetsRowId,
    endItemInlineSlotAssetsRowId,
    inlineSlotIdForAssets,
    onExpandedChange,
    slotInlineDetailsOpen,
    onSlotAssetsInlineDetailsClose,
  ]);

  // Handle card click - collapse when expanded and clicking elsewhere
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't handle if clicking on interactive elements (buttons, inputs, etc.)
    const target = e.target as HTMLElement;

    // Check if clicking on interactive elements - these should not trigger collapse
    const isInteractiveElement =
      target.closest('button') ||
      target.closest('[role="button"]') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('a');

    if (isInteractiveElement) {
      return;
    }

    // If clicking on the content Stack (middle section), it will handle its own click (don't collapse here)
    if (target.closest('[data-cursor-element-id="cursor-el-195"]')) {
      return;
    }

    // If clicking on the timing section, it will handle its own click (don't collapse here)
    // The timing section is the first child Stack inside the Card's first child Stack
    const cardFirstChild = cardRef.current?.firstElementChild;
    const timingSection = cardFirstChild?.firstElementChild;
    if (timingSection && (timingSection.contains(target) || timingSection === target)) {
      return;
    }

    // If clicking on the right section (ItemIcons), it has its own handler - don't collapse
    if (target.closest('[data-cursor-element-id="cursor-el-1"]')) {
      return;
    }

    // If expanded, collapse when clicking elsewhere on the card
    if (isExpanded) {
      onExpandedChange?.(null);
    }
  };

  const handleFlowControlInlineMediaActivate = useCallback(() => {
    if (!flowControlInlineMedia) return;
    if (startEndItem && isStartItem && startItemInlineSlotAssetsRowId) {
      onFlowControlInlineMediaToggle?.(startItemInlineSlotAssetsRowId);
      onClick?.(true);
      return;
    }
    if (startEndItem && !isStartItem && endItemInlineSlotAssetsRowId) {
      onFlowControlInlineMediaToggle?.(endItemInlineSlotAssetsRowId);
      onClick?.(true);
      return;
    }
    if (slot?.id) {
      onFlowControlInlineMediaToggle?.(slot.id);
      onClick?.(true);
    }
  }, [
    flowControlInlineMedia,
    startEndItem,
    isStartItem,
    startItemInlineSlotAssetsRowId,
    endItemInlineSlotAssetsRowId,
    slot?.id,
    onFlowControlInlineMediaToggle,
    onClick,
  ]);

  const flowControlPlayIconActivate = flowControlInlineMedia ? handleFlowControlInlineMediaActivate : undefined;

  /** Middle row (title) and Session/Break right column share this so small-screen Slot Assets taps behave the same. */
  const handleMiddleSectionRowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (blockSlotAssetsInlineInteractions) {
      return;
    }
    if (variant === 'ghost') {
      return;
    }
    if (flowControlInlineMedia) {
      handleFlowControlInlineMediaActivate();
      return;
    }
    if (flowControlInlinePerform && slot?.id) {
      const nextKey = flowControlPerformExpandedKey === slot.id ? null : slot.id;
      onFlowControlPerformExpand?.(nextKey);
      onClick?.(true);
      if (isSmallScreen) {
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
          if (!el) {
            const selectors = [
              `[data-flow-slot-id="${slot.contentType === 'Session' ? slot.content?.id : slot.id}"]`,
              `#flow-slot-${slot.contentType === 'Session' ? slot.content?.id : slot.id}`,
              `#slot-${slot.id}`,
            ];
            const foundEl = selectors.map((sel) => document.querySelector(sel)).find((node): node is Element => !!node);
            if (foundEl && 'scrollIntoView' in foundEl) {
              foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else if ('scrollIntoView' in el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
      return;
    }
    // Don't allow expansion for start/end items (non–flow-control)
    if (startEndItem) {
      if (
        startEndItem &&
        !isStartItem &&
        onFlowEndSlotAssetsInlinePress &&
        startRowMediaOpenDetailsMode === 'flowSlotAssets' &&
        (isSmallScreen ? middleSectionOpensOverlayOnSmallScreen : true)
      ) {
        onFlowEndSlotAssetsInlinePress();
        return;
      }
      if (
        onFlowStartSlotAssetsInlinePress &&
        isSmallScreen &&
        middleSectionOpensOverlayOnSmallScreen &&
        startRowMediaOpenDetailsMode === 'flowSlotAssets'
      ) {
        onFlowStartSlotAssetsInlinePress();
        return;
      }
      onClick?.(true);
      return;
    }
    if (slot?.id) {
      // Large screen: middle section opens inline timing (same as left column), not the edit form
      if (!isSmallScreen) {
        if (startRowMediaOpenDetailsMode === 'flowSlotAssets') {
          onExpandedChange?.(null);
          onClick?.(true);
          return;
        }
        if (startRowMediaOpenDetailsMode === 'flowControl') {
          onExpandedChange?.(null);
          onClick?.(true);
          return;
        }
        if (slot && isOrganizeMode(mode)) {
          onExpandedChange?.(null);
          onShowTimingAlert?.();
          return;
        }
        if (isEditingTiming && expandedSlotId === slot.id) {
          onFormTypeChange?.(slot.id, null);
          return;
        }
        onFormTypeChange?.(slot.id, 'timing');
        if (!isExpanded || expandedFormType !== 'timing') {
          onClick?.(true);
        }
        return;
      }

      // Simple toggle: if edit form is already open, close it; otherwise open it
      if (isEditingEdit && expandedSlotId === slot.id) {
        // Close edit form and collapse slot; also hide inline details (same as first-click expand)
        onFormTypeChange?.(slot.id, null);
        if (middleSectionOpensOverlayOnSmallScreen && isSmallScreen) {
          onSlotAssetsInlineDetailsClose?.(slot.id);
          onClick?.(false);
        }
        return;
      } else {
        // Close any other form, expand slot, open edit form
        onFormTypeChange?.(slot.id, 'new');

        // On small screens: expand and focus. With overlay+inline mode: toggle inline details and select without opening overlay
        if (isSmallScreen) {
          // Scroll to slot to focus it
          requestAnimationFrame(() => {
            const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
            if (!el) {
              const selectors = [
                `[data-flow-slot-id="${slot.contentType === 'Session' ? slot.content?.id : slot.id}"]`,
                `#flow-slot-${slot.contentType === 'Session' ? slot.content?.id : slot.id}`,
                `#slot-${slot.id}`,
              ];
              const foundEl = selectors
                .map((sel) => document.querySelector(sel))
                .find((node): node is Element => !!node);
              if (foundEl && 'scrollIntoView' in foundEl) {
                foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            } else if ('scrollIntoView' in el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
          if (middleSectionOpensOverlayOnSmallScreen) {
            onSlotAssetsInlineDetailsToggle?.(slot.id);
            onClick?.(false);
          }
          return;
        }
      }
    }
    // On large screens: only select the slot if not expanding (to avoid opening overlay when expanding)
    if (!isExpanded || expandedFormType !== 'new') {
      onClick?.(true);
    }
  };

  // Show "not published yet" only if no session data is available for the current mode
  const [showUnpublished, setShowUnpublished] = useState(false);
  useEffect(() => {
    if (isUnpublishedSession) {
      const timer = setTimeout(() => setShowUnpublished(true), 250);
      return () => clearTimeout(timer);
    } else if (showUnpublished) {
      // Only set to false if it's currently true to avoid unnecessary updates
      setShowUnpublished(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnpublishedSession]);
  const showUnavailable = showUnpublished || isDeletedSession;
  const title = staticItem
    ? staticItem?.title
    : slot?.contentType === 'Session'
      ? isDeletedSession
        ? t('common.sessionDeleted')
        : showUnpublished
          ? `Content ${t('common.notPublishedYet')}.`
          : (sessionContent?.title ?? '')
      : slot?.title;
  const isUntimedHeader =
    startEndItem &&
    isStartItem &&
    (isUntimed === true || staticItem?.title === t('flows.detail.slotList.untimedHeader'));
  const isEndItem = startEndItem && !isStartItem;
  const flowControlSegmentKind = useMemo((): FlowControlSegmentKind | undefined => {
    if (!rightSectionPlayIcon) {
      return undefined;
    }
    if (slot) {
      return slot.contentType === 'Break' ? 'break' : 'session';
    }
    if (isStartItem) {
      return 'flow_intro';
    }
    if (isEndItem) {
      return 'flow_outro';
    }
    return undefined;
  }, [rightSectionPlayIcon, slot, isStartItem, isEndItem]);
  const flowControlSegmentAnchor = useMemo((): SlotSegmentAnchor => {
    if (!rightSectionPlayIcon || slot) {
      return 'slot';
    }
    return isStartItem ? 'flow_start' : 'flow_end';
  }, [rightSectionPlayIcon, slot, isStartItem]);
  const { isActive: isSegmentPlaying, isPreloaded: isSegmentPreloaded } = useFlowControlSegmentStatus(
    flowControlSegmentAnchor,
    flowControlSegmentKind,
    slot?.id,
  );
  const nextCueSegmentExpandId = useFlowControlNextCueSegmentExpandId();
  const flowControlRowExpandId = slot?.id ?? startItemInlineSlotAssetsRowId ?? endItemInlineSlotAssetsRowId;
  const isNextCueSegment =
    Boolean(rightSectionPlayIcon) &&
    isFlowControlNextCueSegmentRow(flowControlRowExpandId, nextCueSegmentExpandId, isSegmentPlaying);
  const flowControlSegmentAccent = resolveFlowControlSegmentAccent(
    Boolean((flow ?? flowStore)?.liveContext?.isRunning),
  );
  const isEndItemFlowSlotAssetsClickable =
    isEndItem && startRowMediaOpenDetailsMode === 'flowSlotAssets' && Boolean(onFlowEndSlotAssetsInlinePress);
  const isEndItemFlowControlClickable = isEndItem && flowControlInlineMedia && Boolean(endItemInlineSlotAssetsRowId);
  const isMiddleSectionClickable =
    !isUntimedHeader &&
    !blockSlotAssetsInlineInteractions &&
    variant !== 'ghost' &&
    ((flowControlInlinePerform || flowControlInlineMedia) && startEndItem && isStartItem && !isUntimedHeader
      ? true
      : isEndItemFlowSlotAssetsClickable || isEndItemFlowControlClickable
        ? true
        : startEndItem && onShowFlowDetails && !rightSectionOnlyPublishIcon && isOrganizeMode(mode)
          ? true
          : startEndItem && !isUntimedHeader
            ? true
            : Boolean(slot));
  const isFlowSlotAssetsTab = startRowMediaOpenDetailsMode === 'flowSlotAssets';
  const isFlowScheduleLikeTab =
    !rightSectionPlayIcon && !isFlowSlotAssetsTab && startRowMediaOpenDetailsMode !== 'flowControl';
  const startEndBackground = alpha(theme.palette.secondary.main, 0.2);
  const shouldUseStartEndBackground =
    !rightSectionPlayIcon &&
    !(
      (isFlowSlotAssetsTab && (isEndItem || (isStartItem && !isUntimedHeader))) ||
      (isFlowScheduleLikeTab && isEndItem)
    ) &&
    (isEndItem || (isStartItem && !isUntimedHeader));

  let time = '';
  // Hide time for untimed slots
  if (!isUntimed) {
    if (slot?.computedOffsetMinutes !== undefined && flowStartOffset !== undefined) {
      time = minuteOffsetToTime(flowStartOffset + slot.computedOffsetMinutes);
    } else if (staticItem?.time) {
      time = staticItem.time;
    }
  }
  const slotTimeVisible = useSlotTimeVisible({
    slot,
    staticTime: time,
    isUntimed,
    hideTime,
    forceTimedPreview: layoutForceTimedPreview,
  });
  const storedStartTime = useMemo(() => {
    if (!eventSlotIdParam) return time;
    const baseFlow = flowFromStore ?? flow;
    const startOffset = baseFlow?.slot?.startOffsetMinutes ?? 0;
    return minuteOffsetToTime(startOffset);
  }, [eventSlotIdParam, flowFromStore, flow, time]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging &&
      originalHeightRef.current && {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        height: originalHeightRef.current,
        minHeight: originalHeightRef.current,
        maxHeight: originalHeightRef.current,
        flexShrink: 0,
        boxSizing: 'border-box' as const,
      }),
  };

  const handleInlineFormClose = () => {
    setEditingTiming?.(false);
    // Reset form type when closing timing form
    if (slot?.id && expandedFormType === 'timing') {
      onFormTypeChange?.(slot.id, null);
    }
  };

  const handleEditFormClose = () => {
    // Reset form type when closing edit form
    if (slot?.id && expandedFormType === 'new') {
      onFormTypeChange?.(slot.id, null);
    }
  };

  // Note: Inline timing form is now rendered inside the expanded section below

  return (
    <div
      style={
        isHiddenGapSlot
          ? {
              marginTop: `-${theme.spacing(0.5)}`,
              marginBottom: `-${theme.spacing(0.5)}`,
              height: 0,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <Box
        ref={(node: HTMLDivElement | null) => {
          cardRef.current = node;
          setNodeRef(node);
        }}
        style={style}
        {...(variant !== 'ghost' && !isDropTarget ? { ...attributes } : {})}
        {...(variant !== 'ghost' && !isDropTarget ? { ...listeners } : {})}
        tabIndex={isUntimedHeader ? -1 : undefined}
        sx={{
          ...(isDragging && {
            opacity: 0,
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
          }),
          ...(isPlaceholder && {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
          }),
          ...(isHiddenDropTarget && {
            opacity: 0,
            pointerEvents: 'auto',
            height: 0,
            minHeight: 0,
            maxHeight: 0,
            padding: 0,
            margin: 0,
            border: 'none',
          }),
          ...(isHiddenGapSlot && {
            height: 0,
            minHeight: 0,
            maxHeight: 0,
            padding: 0,
            margin: 0,
            border: 'none',
            boxShadow: 'none',
            pointerEvents: 'none',
            visibility: 'hidden',
            overflow: 'hidden',
          }),
          height:
            isDragging && originalHeightRef.current
              ? originalHeightRef.current
              : placeholderHeight !== undefined
                ? `${placeholderHeight}px`
                : heightWithGap,
          ...(isDragging &&
            originalHeightRef.current && {
              minHeight: originalHeightRef.current,
              maxHeight: originalHeightRef.current,
              flexShrink: 0,
              boxSizing: 'border-box',
            }),
          ...(placeholderHeight !== undefined && {
            minHeight: `${placeholderHeight}px`,
            maxHeight: `${placeholderHeight}px`,
          }),
          ...(hasGapBefore &&
            placeholderHeight === undefined && {
              minHeight: heightWithGap,
            }),
        }}
      >
        <Stack direction="column" height="100%">
          {hasGapBefore && gapBefore ? (
            <Box sx={{ pb: 1 }}>
              <Card
                elevation={0}
                sx={{
                  height: '32px',
                  minHeight: '32px',
                  border: '1px dashed',
                  borderColor: 'divider',
                  backgroundColor: 'slot.gap.active',
                  borderRadius: startEndItem ? 0 : undefined,
                  overflow: 'hidden',
                }}
              >
                <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ minWidth: leftColumnMinWidth, flexShrink: 0, height: '100%' }}
                  >
                    <SlotListLeftLeading
                      enabled={dragHandleColumnInLayout}
                      showDragHandle={false}
                      showLockIndicator={false}
                      isDragging={isDragging}
                      dragDisabled={shouldDisableDrag}
                      attributes={attributes}
                      listeners={listeners}
                    />
                    <Stack direction="column" alignItems="center" justifyContent="center" sx={{ minWidth: 0, flex: 1 }}>
                      {!hideGapTiming ? (
                        <>
                          {!hideGapStartTime ? (
                            <Typography
                              variant="slotStartTime"
                              color="text.secondary"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                              }}
                            >
                              {gapTime}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="slotDuration"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {`${gapBefore.duration} min`}
                          </Typography>
                        </>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Divider orientation="vertical" flexItem />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ p: 1, flex: 1, minWidth: 0 }}
                  >
                    {setSelectedSlot && onShowForm && !hideGapSlotWizardAndBreak ? (
                      <Actions
                        offset={gapBefore.startOffset}
                        duration={gapBefore.duration}
                        setSelectedSlot={setSelectedSlot}
                        showWizardAndBreak={!hideGapSlotWizardAndBreak}
                        onShowForm={(slotType) => {
                          if (slot) {
                            onShowForm(`gap-before-${slot.id}`, gapBefore.startOffset, slotType ?? 'Session', slot.id);
                            return;
                          }
                          const gapId = `gap-after-${gapBeforeInsertAfterSlotId ?? 'end'}`;
                          onShowForm(
                            gapId,
                            gapBefore.startOffset,
                            slotType ?? 'Session',
                            undefined,
                            gapBeforeInsertAfterSlotId,
                          );
                        }}
                      />
                    ) : null}
                  </Stack>
                </Stack>
              </Card>
            </Box>
          ) : null}
          {customContent ? (
            customContent
          ) : (
            <Card
              ref={(node) => {
                cardRef.current = node;
              }}
              onClick={showUnavailable || isUntimedHeader ? undefined : handleCardClick}
              elevation={0}
              data-testid={
                slot || startItemInlineSlotAssetsRowId || endItemInlineSlotAssetsRowId ? 'flow-slot' : undefined
              }
              data-slot-id={slot?.id ?? startItemInlineSlotAssetsRowId ?? endItemInlineSlotAssetsRowId}
              data-flow-slot-id={slot?.contentType === 'Session' ? slot.content?.id : undefined}
              data-start-end-item={startEndItem ? 'true' : undefined}
              sx={{
                'cursor': isUntimedHeader ? 'default' : variant === 'normal' ? 'pointer' : 'default',
                '&:hover': isUntimedHeader
                  ? undefined
                  : variant === 'normal' && !isDragging && !shouldUseStartEndBackground
                    ? {
                        transform: !isOrganizeMode(mode) ? 'scale(1.02)' : 'none',
                        bgcolor:
                          slot?.contentType === 'Break'
                            ? showSelectedChrome
                              ? 'slot.break.selected'
                              : 'slot.break.hover'
                            : showSelectedChrome
                              ? 'slot.normal.selected'
                              : 'slot.normal.hover',
                      }
                    : undefined,
                'bgcolor':
                  variant === 'normal'
                    ? isUntimedHeader
                      ? 'transparent'
                      : rightSectionPlayIcon && isSegmentPlaying
                        ? flowControlActiveSegmentBackground(theme, flowControlSegmentAccent)
                        : rightSectionPlayIcon && isNextCueSegment
                          ? flowControlNextCueSegmentBackground(theme)
                          : shouldUseStartEndBackground
                            ? startEndBackground
                            : slot?.contentType === 'Break' ||
                                (!startEndItem && (title === 'Start of Flow' || title === 'End of Flow'))
                              ? showSelectedChrome || isEditingTiming
                                ? 'slot.break.selected'
                                : 'slot.break.active'
                              : showSelectedChrome || isEditingTiming
                                ? 'slot.normal.selected'
                                : 'slot.normal.active'
                    : 'slot.gap.active',
                // Control segments: playing / Next use fill only (no accent outline). Items keep colored borders.
                'outlineWidth': isUntimedHeader
                  ? 0
                  : rightSectionPlayIcon && (isSegmentPlaying || isNextCueSegment)
                    ? '1px'
                    : isSegmentPlaying || isNextCueSegment || showSelectedChrome || isEditingTiming
                      ? '2px'
                      : '1px',
                'outlineColor': offsetViolation
                  ? 'error.main'
                  : shortenedBy > 0
                    ? 'warning.main'
                    : rightSectionPlayIcon && (isSegmentPlaying || isNextCueSegment)
                      ? startEndItem || slot?.contentType === 'Break'
                        ? 'black'
                        : 'divider'
                      : isSegmentPlaying
                        ? `${flowControlSegmentAccent}.main`
                        : isNextCueSegment
                          ? 'success.main'
                          : showSelectedChrome || isEditingTiming
                            ? 'secondary.main'
                            : startEndItem || slot?.contentType === 'Break'
                              ? 'black'
                              : 'divider',
                'outlineStyle': isUntimedHeader ? 'none' : variant === 'ghost' || showUnavailable ? 'dashed' : 'solid',
                'overflow': 'hidden',
                'borderRadius': startEndItem ? 0 : undefined, // Remove rounded corners for start/end items
                'height': baseHeight,
                'minHeight': baseHeight,
              }}
            >
              <Stack direction="column" sx={{ height: '100%', minHeight: 0 }}>
                <Stack
                  direction="row"
                  alignItems="stretch"
                  sx={{
                    flex: hasExpandedActionRow ? '1 1 0' : undefined,
                    height: hasExpandedActionRow ? undefined : '100%',
                    minHeight: 0,
                  }}
                >
                  <Tooltip
                    title={
                      slot || onTimingSectionClick
                        ? startEndItem
                          ? onStartTimeChange
                            ? t('flows.detail.slotList.detail.tooltips.editFlowStartTime')
                            : t('flows.detail.slotList.detail.tooltips.editFlowEndTime')
                          : t('flows.detail.slotList.detail.tooltips.editSlotTiming')
                        : ''
                    }
                    disableHoverListener={
                      isUntimedHeader || blockSlotAssetsInlineInteractions || !(slot || onTimingSectionClick)
                    }
                    leaveDelay={0}
                    disableInteractive
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isUntimedHeader) {
                          return;
                        }
                        if (blockSlotAssetsInlineInteractions) {
                          return;
                        }
                        // For start-item, activate time picker when clicking anywhere in the left section
                        if (startEndItem && onStartTimeChange && !isEditingStartTime) {
                          setIsEditingStartTime(true);
                          return;
                        }
                        if (slot && !isOrganizeMode(mode)) {
                          if (slot.id) {
                            // Simple toggle: if timing form is already open, close it; otherwise open it
                            if (isEditingTiming && expandedSlotId === slot.id) {
                              // Close timing form and collapse slot
                              onFormTypeChange?.(slot.id, null);
                            } else {
                              // Close any other form, expand slot, open timing form
                              onFormTypeChange?.(slot.id, 'timing');
                              if (isSmallScreen && middleSectionOpensOverlayOnSmallScreen && slotInlineDetailsOpen) {
                                onSlotAssetsInlineDetailsClose?.(slot.id);
                              }

                              // On small screens: expand and focus, but don't select (to avoid opening overlay)
                              if (isSmallScreen) {
                                // Scroll to slot to focus it
                                requestAnimationFrame(() => {
                                  const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
                                  if (!el) {
                                    const selectors = [
                                      `[data-flow-slot-id="${slot.contentType === 'Session' ? slot.content?.id : slot.id}"]`,
                                      `#flow-slot-${slot.contentType === 'Session' ? slot.content?.id : slot.id}`,
                                      `#slot-${slot.id}`,
                                    ];
                                    const foundEl = selectors
                                      .map((sel) => document.querySelector(sel))
                                      .find((node): node is Element => !!node);
                                    if (foundEl && 'scrollIntoView' in foundEl) {
                                      foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                  } else if ('scrollIntoView' in el) {
                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }
                                });
                                // Don't call onClick on small screens to avoid opening overlay
                                return;
                              }
                            }
                          }
                        } else if (slot && isOrganizeMode(mode)) {
                          if (startRowMediaOpenDetailsMode === 'flowControl' && !isSmallScreen) {
                            onExpandedChange?.(null);
                            onClick?.(true);
                            return;
                          }
                          // In organize mode, collapse any expanded slot
                          onExpandedChange?.(null);
                          onShowTimingAlert?.();
                        } else {
                          // For start/end items, collapse any expanded slot
                          onExpandedChange?.(null);
                          onTimingSectionClick?.();
                        }
                      }}
                      sx={{
                        'minWidth': leftColumnMinWidth,
                        'flexShrink': 0,
                        'alignSelf': 'stretch',
                        'minHeight': '32px',
                        ...(timePaddingX !== undefined && { px: timePaddingX }),
                        'cursor': isUntimedHeader
                          ? 'default'
                          : blockSlotAssetsInlineInteractions
                            ? 'default'
                            : slot || onTimingSectionClick || (startEndItem && onStartTimeChange)
                              ? 'pointer'
                              : 'default',
                        'boxShadow': shortenedBy > 0 ? `0px 0px 8px ${theme.palette.warning.main} inset` : undefined,
                        '&:hover':
                          !blockSlotAssetsInlineInteractions && slot && !isOrganizeMode(mode)
                            ? {
                                boxShadow:
                                  shortenedBy > 0 ? undefined : `0px 0px 8px ${theme.palette.info.light} inset`,
                              }
                            : undefined,
                      }}
                    >
                      <SlotListLeftLeading
                        enabled={dragHandleColumnInLayout}
                        showDragHandle={showRowDragHandle}
                        showLockIndicator={showRowLockInLeading}
                        isDragging={isDragging}
                        dragDisabled={dragHandleInteractionDisabled}
                        attributes={attributes}
                        listeners={listeners}
                      />
                      <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0}
                        sx={{ height: '100%', minWidth: 0, flex: 1, minHeight: 0 }}
                      >
                        {refreshing ? (
                          <CircularProgress />
                        ) : (
                          <>
                            {/* Show static title in left section ONLY for untimed header (Session Pool) */}
                            {startEndItem && isStartItem && isUntimed && staticItem?.title ? (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}
                              >
                                {staticItem.title}
                              </Typography>
                            ) : (
                              <Stack
                                direction="column"
                                alignItems="center"
                                spacing={0}
                                sx={{
                                  'width': '100%',
                                  'minHeight': 0,
                                  '& > *:not(:first-of-type)': {
                                    marginTop: '-4px',
                                  },
                                }}
                              >
                                {eventSlotStartItemDayLines && event && flowToUse ? (
                                  showEventSlotFlowStartDayReadOnly ? (
                                    <Stack
                                      alignItems="center"
                                      justifyContent="center"
                                      spacing={0}
                                      sx={{ width: '100%' }}
                                    >
                                      <Typography
                                        variant="slotStartTime"
                                        color="text.secondary"
                                        sx={{
                                          textAlign: 'center',
                                          width: '100%',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          lineHeight: 1,
                                        }}
                                      >
                                        {eventSlotStartItemDayLines.multiDay
                                          ? t2('flows.detail.slotList.eventDayNumberLine', {
                                              dayNumber: eventSlotStartItemDayLines.dayNumber,
                                            })
                                          : t2('flows.detail.slotList.eventDayDateLabel')}
                                      </Typography>
                                      <Typography
                                        variant="slotStartTime"
                                        color="text.secondary"
                                        sx={{
                                          textAlign: 'center',
                                          width: '100%',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          lineHeight: 1,
                                        }}
                                      >
                                        {eventSlotStartItemDayLines.dateStr}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <FormControl
                                      variant="standard"
                                      fullWidth
                                      size="small"
                                      onClick={(e) => e.stopPropagation()}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      sx={{
                                        'minWidth': 0,
                                        'display': 'flex',
                                        'alignItems': 'center',
                                        'justifyContent': 'center',
                                        'mb': 0.25,
                                        '& .MuiInput-underline:before': {
                                          display: 'none',
                                        },
                                        '& .MuiInput-underline:after': {
                                          display: 'none',
                                        },
                                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                          display: 'none',
                                        },
                                        '&:hover .MuiInput-underline:before': {
                                          display: 'none',
                                        },
                                        '&:focus .MuiInput-underline:before': {
                                          display: 'none',
                                        },
                                        '&:focus-within .MuiInput-underline:before': {
                                          display: 'none',
                                        },
                                      }}
                                    >
                                      <Select
                                        value={flowToUse.slot?.day?.id || event.days[0]?.id || ''}
                                        onChange={(e) => {
                                          const newDayId = e.target.value;
                                          if (onDayChange) {
                                            onDayChange(newDayId);
                                          }
                                          if (onPreviewDayChange) {
                                            onPreviewDayChange(newDayId);
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        renderValue={(value) => {
                                          const selectedDayId = String(value ?? '');
                                          const dayIdx = event.days.findIndex((d) => d.id === selectedDayId);
                                          if (dayIdx < 0 || !event.startDate) {
                                            return '';
                                          }
                                          const multiDay = event.days.length > 1;
                                          return (
                                            <Stack alignItems="center" spacing={0} sx={{ width: '100%', py: 0.25 }}>
                                              <Typography
                                                variant="slotStartTime"
                                                color="text.secondary"
                                                sx={{
                                                  textAlign: 'center',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  lineHeight: 1,
                                                }}
                                              >
                                                {multiDay
                                                  ? t2('flows.detail.slotList.eventDayNumberLine', {
                                                      dayNumber: dayIdx + 1,
                                                    })
                                                  : t2('flows.detail.slotList.eventDayDateLabel')}
                                              </Typography>
                                              <Typography
                                                variant="slotStartTime"
                                                color="text.secondary"
                                                sx={{
                                                  textAlign: 'center',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  lineHeight: 1,
                                                }}
                                              >
                                                {format(addDays(event.startDate, dayIdx), 'dd/MM')}
                                              </Typography>
                                            </Stack>
                                          );
                                        }}
                                        sx={{
                                          'width': '100%',
                                          'display': 'flex',
                                          'alignItems': 'center',
                                          '&::before': {
                                            display: 'none',
                                          },
                                          '&::after': {
                                            display: 'none',
                                          },
                                          '&:hover::before': {
                                            display: 'none',
                                          },
                                          '&:hover::after': {
                                            display: 'none',
                                          },
                                          '&.Mui-focused::before': {
                                            display: 'none',
                                          },
                                          '&.Mui-focused::after': {
                                            display: 'none',
                                          },
                                          '& .MuiSelect-select': {
                                            padding: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            minHeight: 0,
                                            ...theme.typography.slotStartTime,
                                            color: 'text.secondary',
                                          },
                                          '& .MuiInputBase-input': {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            minHeight: 0,
                                          },
                                          '& .MuiInputBase-root': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: 0,
                                          },
                                        }}
                                        slotProps={{
                                          input: {
                                            sx: {
                                              'display': 'flex',
                                              'alignItems': 'center',
                                              'justifyContent': 'center',
                                              'minHeight': 0,
                                              '&::before': {
                                                display: 'none',
                                              },
                                              '&::after': {
                                                display: 'none',
                                              },
                                              '&:hover::before': {
                                                display: 'none',
                                              },
                                              '&:hover::after': {
                                                display: 'none',
                                              },
                                              '&.Mui-focused::before': {
                                                display: 'none',
                                              },
                                              '&.Mui-focused::after': {
                                                display: 'none',
                                              },
                                            },
                                          },
                                        }}
                                      >
                                        {event.days.map((day, i) => (
                                          <MenuItem key={`day-${i}`} value={day.id}>
                                            {t2('event.detail.addFlow.values.eventDay.value', {
                                              dayId: `${i + 1}`,
                                              date: format(addDays(event.startDate, i), 'dd/MM'),
                                            })}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )
                                ) : null}
                                {!(eventSlotStartItemDayLines && event && flowToUse) ? (
                                  <>
                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                                      {locked &&
                                        !dragHandleColumnInLayout &&
                                        (!startEndItem || !onStartTimeChange) &&
                                        !isInlineTimingSectionOpen && (
                                          <LockClockSharp sx={{ fontSize: '0.875rem' }} color="action" />
                                        )}
                                      {isEditingStartTime && startEndItem && onStartTimeChange ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 'auto',
                                            minHeight: 'auto',
                                          }}
                                        >
                                          <TimePicker
                                            ampm={false}
                                            views={['hours', 'minutes']}
                                            value={
                                              pendingStartTime
                                                ? parse(pendingStartTime, 'HH:mm', new Date())
                                                : time
                                                  ? parse(time, 'HH:mm', new Date())
                                                  : null
                                            }
                                            timeSteps={{ minutes: 1 }}
                                            icon={EditOutlined}
                                            forceDesktopMode={true}
                                            open={isEditingStartTime}
                                            onOpen={() => {
                                              didAcceptStartTimeRef.current = false;
                                              initialStartTimeRef.current = storedStartTime || time;
                                              setIsEditingStartTime(true);
                                            }}
                                            onClose={() => {
                                              if (didAcceptStartTimeRef.current) {
                                                didAcceptStartTimeRef.current = false;
                                                initialStartTimeRef.current = null;
                                                return;
                                              }
                                              setPendingStartTime(null);
                                              setIsEditingStartTime(false);
                                              // Clear preview when closing without accepting
                                              if (onPreviewStartTimeChange && initialStartTimeRef.current) {
                                                onPreviewStartTimeChange(initialStartTimeRef.current);
                                              }
                                              initialStartTimeRef.current = null;
                                            }}
                                            onChange={(value) => {
                                              if (value) {
                                                const newTime = format(value, 'HH:mm');
                                                setPendingStartTime(newTime);
                                                // Update preview flow immediately
                                                if (onPreviewStartTimeChange) {
                                                  onPreviewStartTimeChange(newTime);
                                                }
                                              }
                                            }}
                                            onAccept={(value) => {
                                              if (value && onStartTimeChange) {
                                                const newTime = format(value, 'HH:mm');
                                                didAcceptStartTimeRef.current = true;
                                                onStartTimeChange(newTime);
                                                setPendingStartTime(null);
                                                setIsEditingStartTime(false);
                                                initialStartTimeRef.current = null;
                                              }
                                            }}
                                            slotProps={{
                                              popper: {
                                                modifiers: [
                                                  {
                                                    name: 'offset',
                                                    options: {
                                                      offset: [100, 8],
                                                    },
                                                  },
                                                  {
                                                    name: 'preventOverflow',
                                                    enabled: true,
                                                    options: {
                                                      boundary: 'viewport',
                                                      padding: 8,
                                                    },
                                                  },
                                                  {
                                                    name: 'flip',
                                                    enabled: true,
                                                  },
                                                ],
                                                placement: 'bottom-start',
                                                onClick: (e: React.MouseEvent) => {
                                                  e.stopPropagation();
                                                },
                                              },
                                              actionBar: {
                                                actions: ['cancel', 'accept'],
                                              },
                                              textField: {
                                                variant: 'standard',
                                                readOnly: true,
                                                sx: {
                                                  'width': '60px',
                                                  'maxWidth': 'none',
                                                  '& .MuiInputBase-root': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    height: 'auto',
                                                    minHeight: 'auto',
                                                    fontFamily: 'Roboto',
                                                    lineHeight: 1.0,
                                                    letterSpacing: '0.033em',
                                                    padding: 0,
                                                    margin: 0,
                                                  },
                                                  '& .MuiInputBase-input': {
                                                    py: 0,
                                                    px: 0,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                    color: 'text.secondary',
                                                    fontFamily: 'Roboto',
                                                    lineHeight: 1.0,
                                                    letterSpacing: '0.033em',
                                                    height: 'auto',
                                                    minHeight: 'auto',
                                                    cursor: 'pointer',
                                                    pointerEvents: 'none',
                                                  },
                                                  // Target MUI TimePicker section content (the actual displayed text)
                                                  '& .MuiPickersInputBase-root': {
                                                    'fontSize': '0.875rem !important',
                                                    'fontWeight': '600 !important',
                                                    'fontFamily': 'Roboto !important',
                                                    'lineHeight': '1.0 !important',
                                                    'letterSpacing': '0.033em !important',
                                                    'gap': 0,
                                                    '&::before': {
                                                      display: 'none',
                                                    },
                                                    '&::after': {
                                                      display: 'none',
                                                    },
                                                    '&:hover::before': {
                                                      display: 'none',
                                                    },
                                                    '&:hover::after': {
                                                      display: 'none',
                                                    },
                                                    '&.Mui-focused::before': {
                                                      display: 'none',
                                                    },
                                                    '&.Mui-focused::after': {
                                                      display: 'none',
                                                    },
                                                    '& .MuiPickersInputBase-sectionsContainer': {
                                                      marginRight: 0,
                                                      paddingRight: 0,
                                                      paddingBottom: 0,
                                                    },
                                                  },
                                                  '& .MuiPickersSectionList-sectionContent': {
                                                    fontSize: '0.875rem !important',
                                                    fontWeight: '600 !important',
                                                    fontFamily: 'Roboto !important',
                                                    lineHeight: '1.0 !important',
                                                    letterSpacing: '0.033em !important',
                                                    color: 'text.secondary !important',
                                                  },
                                                  '& .MuiPickersInputBase-sectionContent': {
                                                    fontSize: '0.875rem !important',
                                                    fontWeight: '600 !important',
                                                    fontFamily: 'Roboto !important',
                                                    lineHeight: '1.0 !important',
                                                    letterSpacing: '0.033em !important',
                                                    color: 'text.secondary !important',
                                                  },
                                                  // Style the TimePicker icon to match 14px size and remove spacing
                                                  '& .MuiInputAdornment-root': {
                                                    'marginLeft': 0,
                                                    'marginRight': 0,
                                                    'paddingLeft': 0,
                                                    'paddingRight': 0,
                                                    '& .MuiIconButton-root': {
                                                      'padding': '2px',
                                                      'margin': 0,
                                                      'marginLeft': 0,
                                                      '& .MuiSvgIcon-root': {
                                                        fontSize: '16px !important',
                                                        width: '16px !important',
                                                        height: '16px !important',
                                                      },
                                                    },
                                                  },
                                                  '& .MuiPickersInputAdornment-root': {
                                                    'marginLeft': 0,
                                                    'marginRight': 0,
                                                    'paddingLeft': 0,
                                                    'paddingRight': 0,
                                                    '& .MuiIconButton-root': {
                                                      'padding': '2px',
                                                      'margin': 0,
                                                      'marginLeft': 0,
                                                      '& .MuiSvgIcon-root': {
                                                        fontSize: '14px !important',
                                                        width: '14px !important',
                                                        height: '14px !important',
                                                      },
                                                    },
                                                  },
                                                  // Remove all underline styles
                                                  '& .MuiInput-underline:before': {
                                                    display: 'none',
                                                  },
                                                  '& .MuiInput-underline:after': {
                                                    display: 'none',
                                                  },
                                                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                                    display: 'none',
                                                  },
                                                  '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
                                                    display: 'none',
                                                  },
                                                  '&:hover .MuiInput-underline:before': {
                                                    display: 'none',
                                                  },
                                                  '&:hover .MuiInput-underline:after': {
                                                    display: 'none',
                                                  },
                                                  '&:focus .MuiInput-underline:before': {
                                                    display: 'none',
                                                  },
                                                  '&:focus .MuiInput-underline:after': {
                                                    display: 'none',
                                                  },
                                                  '&:focus-within .MuiInput-underline:before': {
                                                    display: 'none',
                                                  },
                                                  '&:focus-within .MuiInput-underline:after': {
                                                    display: 'none',
                                                  },
                                                },
                                              },
                                            }}
                                          />
                                        </Box>
                                      ) : shouldShowStartTime &&
                                        (slotTimeVisible || showFixedStartTimeEditNextToSlotTime) ? (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          justifyContent="center"
                                          spacing={0}
                                          sx={{
                                            width: showFixedStartTimeEditNextToSlotTime ? 60 : 'auto',
                                            minHeight: 24,
                                            gap: 0,
                                          }}
                                        >
                                          <Typography
                                            variant="slotStartTime"
                                            color="text.secondary"
                                            sx={{
                                              ...(showFixedStartTimeEditNextToSlotTime
                                                ? { flex: '1 1 0', minWidth: 0, textAlign: 'center' as const }
                                                : {
                                                    maxWidth: '100%',
                                                    textAlign: 'center' as const,
                                                  }),
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              lineHeight: 1,
                                              cursor:
                                                slot || onTimingSectionClick || (startEndItem && onStartTimeChange)
                                                  ? 'pointer'
                                                  : 'default',
                                            }}
                                          >
                                            <SlotTimeText
                                              slot={slot}
                                              flowStartOffset={flowStartOffset}
                                              staticTime={time}
                                              isUntimed={isUntimed}
                                              hideTime={hideTime}
                                              forceTimedPreview={layoutForceTimedPreview}
                                            />
                                          </Typography>
                                          {showFixedStartTimeEditNextToSlotTime ? (
                                            <Tooltip
                                              title={t('flows.detail.slotTiming.editStartTime')}
                                              leaveDelay={0}
                                              disableInteractive
                                            >
                                              <IconButton
                                                size="medium"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  inlineTimingFormRef.current?.openFixedStartTimePicker();
                                                }}
                                                disabled={isOrganizeMode(mode) || scheduleLiveLockBlocked}
                                                sx={{
                                                  'p': '2px',
                                                  'flexShrink': 0,
                                                  'ml': 0,
                                                  'minWidth': 0,
                                                  // Match MUI TimePicker end-adornment (filled): 16px icon + 2px padding
                                                  '& .MuiSvgIcon-root': {
                                                    fontSize: 16,
                                                    width: 16,
                                                    height: 16,
                                                  },
                                                }}
                                                aria-label={t('flows.detail.slotTiming.editStartTime')}
                                              >
                                                <EditOutlined />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null}
                                        </Stack>
                                      ) : null}
                                    </Stack>
                                    {/* Start row: same as slot rows (`N min`). End row only: total time as `Hh MMm`. */}
                                    {(isStartItemFooter && !isEditingStartTime && flowDuration !== undefined) ||
                                    (isEndItem && (flowDuration ?? 0) > 0) ? (
                                      <Stack direction="column" alignItems="center" spacing={0}>
                                        <Typography
                                          variant="slotDuration"
                                          color="text.secondary"
                                          sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                          }}
                                        >
                                          {isEndItem && flowDuration !== undefined
                                            ? formatDurationHoursMinutesSlot(flowDuration)
                                            : `${flowDuration} min`}
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <SlotDurationDisplay
                                        slot={slot}
                                        staticDuration={staticItem?.duration}
                                        isUntimed={isUntimed}
                                        hideDuration={hideDuration}
                                        forceTimedPreview={layoutForceTimedPreview}
                                        sessionPlannedDurationMinutes={sessionPlannedDurationMinutesForDisplay}
                                      />
                                    )}
                                  </>
                                ) : null}
                              </Stack>
                            )}
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Tooltip>
                  {!isUntimedHeader && <Divider orientation="vertical" flexItem />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      pl: 1,
                      pr: 0,
                      flex: 1,
                      minWidth: 0,
                      pointerEvents: isUntimedHeader ? 'none' : undefined,
                      cursor: isMiddleSectionClickable ? 'pointer' : 'default',
                      touchAction: 'manipulation',
                      // For untimed header, extend to include right section space
                      ...(startEndItem &&
                        isStartItem &&
                        (isUntimed === true || staticItem?.title === t('flows.detail.slotList.untimedHeader')) && {
                          pr: 1,
                        }),
                    }}
                    data-cursor-element-id="cursor-el-195"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isUntimedHeader) {
                        return;
                      }
                      if (blockSlotAssetsInlineInteractions) {
                        return;
                      }
                      if (variant === 'ghost') {
                        return;
                      }
                      if (flowControlInlineMedia && startEndItem && isStartItem && !isUntimedHeader) {
                        if (startItemInlineSlotAssetsRowId) {
                          onFlowControlInlineMediaToggle?.(startItemInlineSlotAssetsRowId);
                        }
                        return;
                      }
                      if (flowControlInlinePerform && startEndItem && isStartItem && !isUntimedHeader) {
                        onFlowControlStartItemMiddleClick?.();
                        return;
                      }
                      handleMiddleSectionRowClick(e);
                    }}
                  >
                    <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                      {/* Event day (event-slot start item) is in the left column above time/duration */}
                      {/* Untimed header / master flow: */}
                      {startEndItem &&
                      isStartItem &&
                      (staticItem?.title === t('flows.detail.slotList.untimedHeader') ||
                        isUntimed === true) ? null : startEndItem && !eventSlotIdParam && isStartItem ? ( // Untimed header - title is shown in left section, Actions component will be shown below
                        // Master flow text (only for timed start item, not untimed header)
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {currentMode === 'PLAN'
                            ? t('flows.detail.masterFlow.editMasterFlow')
                            : t('flows.detail.masterFlow.sharedMasterFlow')}
                        </Typography>
                      ) : title ? (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
                          {slot?.contentType === 'Session' &&
                            slot.content?.type &&
                            !isDeletedSession &&
                            (() => {
                              const iconElement = sessionTypeToIcon(slot.content.type);
                              return (
                                <Stack sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                  {isValidElement(iconElement)
                                    ? cloneElement(iconElement as ReactElement<{ sx?: { fontSize?: string } }>, {
                                        sx: { fontSize: '0.875rem' },
                                      })
                                    : iconElement}
                                </Stack>
                              );
                            })()}
                          {slot?.contentType === 'Session' && slot.content?.isInLibrary && !isDeletedSession && (
                            <Stack sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                              <LibraryAddCheckOutlined fontSize="small" sx={{ fontSize: '0.875rem' }} />
                            </Stack>
                          )}
                          {slot?.contentType === 'Break' && (
                            <Stack sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                              <PauseCircleOutlineTwoTone sx={{ fontSize: '0.875rem' }} color="action" />
                            </Stack>
                          )}
                          {typeof title === 'string' ? (
                            <Typography
                              variant="subtitle2"
                              color={showUnavailable ? 'text.disabled' : 'text.primary'}
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {title}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                color: showUnavailable ? 'text.disabled' : 'text.primary',
                                fontSize: theme.typography.subtitle2.fontSize,
                                fontWeight: theme.typography.subtitle2.fontWeight,
                                lineHeight: theme.typography.subtitle2.lineHeight,
                                fontFamily: theme.typography.subtitle2.fontFamily,
                                letterSpacing: theme.typography.subtitle2.letterSpacing,
                              }}
                            >
                              {title}
                            </Box>
                          )}
                          {sessionSegmentLabel ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flexShrink: 0, ml: 'auto', pr: 1, whiteSpace: 'nowrap' }}
                            >
                              {sessionSegmentLabel}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : showEventSlotFlowStartTitleRow ? (
                        <Stack direction="column" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                          <Typography
                            variant="subtitle2"
                            color="text.primary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {flowToUse?.title ?? ''}
                          </Typography>
                          {flowStartRowModeratorNames ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                              }}
                            >
                              {flowStartRowModeratorNames}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : null}
                      {/* Contributor names display */}
                      {slot?.contentType === 'Session' &&
                        slot.content?.contributors &&
                        slot.content.contributors.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {slot.content.contributors
                              .map((contributor) => contributor.contributor.profiles?.[0]?.displayName)
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        )}
                      {staticItem?.children && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-around"
                          sx={{
                            width: '100%',
                            ...(isUntimedHeader ? { pointerEvents: 'auto' } : {}),
                          }}
                        >
                          {staticItem.children}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                  {/* Divider and right section - only show for regular slots and start/end items */}
                  {/* Don't show divider/right section for untimed header - middle and right are unified */}
                  {((slot && (slot.contentType === 'Session' || slot.contentType === 'Break')) || startEndItem) &&
                  !(
                    startEndItem &&
                    isStartItem &&
                    (isUntimed === true || staticItem?.title === t('flows.detail.slotList.untimedHeader'))
                  ) ? (
                    <>
                      {!rightSectionPlayIcon ? <Divider orientation="vertical" flexItem /> : null}
                      {/* Right section - for regular slots and start/end items */}
                      {slot && (slot.contentType === 'Session' || slot.contentType === 'Break') ? (
                        <Stack
                          data-cursor-element-id="cursor-el-1"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={handleMiddleSectionRowClick}
                          sx={{
                            cursor: 'pointer',
                            ...(rightSectionPlayIcon ? flowControlRightSectionStackSx : rightSectionStackSx),
                          }}
                        >
                          {rightSectionPlayIcon ? (
                            <FlowControlPlayColumnRail onSpacerClick={flowControlPlayIconActivate}>
                              <SlotRightSectionPlayIcon
                                segmentAnchor="slot"
                                segmentKind={slot.contentType === 'Break' ? 'break' : 'session'}
                                slotId={slot.id}
                                onActivate={flowControlPlayIconActivate}
                                ready={isSegmentPreloaded}
                                segmentTitle={typeof title === 'string' ? title : slot.title}
                              />
                            </FlowControlPlayColumnRail>
                          ) : rightSectionOnlyPublishIcon &&
                            slot.contentType === 'Session' &&
                            sessionForPublishButton &&
                            hasPlanDataForPublish ? (
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SessionPublish
                                session={sessionForPublishButton}
                                indicativeOnly={currentMode !== 'PLAN'}
                                onIndicativeClick={onShowPublishAlert}
                                onPublishSuccess={onSessionPublishSuccess}
                              />
                            </Box>
                          ) : rightSectionOnlyPublishIcon && slot.contentType === 'Break' ? null : (
                            <ItemIcons
                              slot={slot}
                              flow={flowOrganize ?? flowFromStore ?? flow}
                              flowOrganize={flowOrganize}
                              sessionContent={sessionContent}
                              allContributors={allContributors}
                              event={event}
                              eventSlotId={eventSlotIdParam ?? undefined}
                            />
                          )}
                        </Stack>
                      ) : startEndItem && isStartItemFooter ? (
                        // Start-item: contributors/media (flow publish is in CollapsibleInfoAlert when event slot)
                        <Stack
                          data-cursor-element-id="cursor-el-1"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Control tab: same expand toggle as middle section / session rows.
                            if (flowControlInlineMedia) {
                              handleFlowControlInlineMediaActivate();
                              return;
                            }
                            if (
                              onFlowStartSlotAssetsInlinePress &&
                              isSmallScreen &&
                              middleSectionOpensOverlayOnSmallScreen &&
                              startRowMediaOpenDetailsMode === 'flowSlotAssets'
                            ) {
                              onFlowStartSlotAssetsInlinePress();
                              return;
                            }
                            if (!rightSectionOnlyPublishIcon && onShowFlowDetails) {
                              setSelectedSlot?.(undefined);
                              setFlowDetailsDirection?.('up');
                              onShowFlowDetails(startRowMediaDetailsMode);
                            }
                          }}
                          sx={{
                            cursor: rightSectionOnlyPublishIcon ? 'default' : 'pointer',
                            ...(rightSectionPlayIcon ? flowControlRightSectionStackSx : rightSectionStackSx),
                          }}
                        >
                          {flowToUse ? (
                            rightSectionPlayIcon ? (
                              <FlowControlPlayColumnRail onSpacerClick={flowControlPlayIconActivate}>
                                <SlotRightSectionPlayIcon
                                  segmentAnchor="flow_start"
                                  segmentKind="flow_intro"
                                  onActivate={flowControlPlayIconActivate}
                                  ready={isSegmentPreloaded}
                                  segmentTitle={typeof title === 'string' ? title : undefined}
                                />
                              </FlowControlPlayColumnRail>
                            ) : (
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={1}
                                sx={{
                                  p: 1,
                                  width: '100%',
                                  minWidth: 0,
                                  height: '100%',
                                  flexShrink: 0,
                                  boxSizing: 'border-box',
                                }}
                              >
                                <>
                                  {(() => {
                                    const contributorColor =
                                      startRowMediaOpenDetailsMode === 'flowSlotAssets'
                                        ? getFlowModeratorStatusIconColor(
                                            flowToUse,
                                            allContributors ?? [],
                                            event?.multiFlow,
                                            eventSlotIdParam ?? undefined,
                                            event,
                                          )
                                        : getFlowContributorColor(flowToUse);
                                    const ContributorIcon = contributorColor === 'primary' ? People : PeopleOutline;
                                    return (
                                      <ContributorIcon
                                        color={contributorColor}
                                        sx={{ height: 20, width: 20 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            onFlowStartSlotAssetsInlinePress &&
                                            isSmallScreen &&
                                            middleSectionOpensOverlayOnSmallScreen &&
                                            startRowMediaOpenDetailsMode === 'flowSlotAssets'
                                          ) {
                                            onFlowStartSlotAssetsInlinePress();
                                            return;
                                          }
                                          if (onShowFlowDetails) {
                                            setSelectedSlot?.(undefined);
                                            setFlowDetailsDirection?.('up');
                                            onShowFlowDetails(
                                              startRowMediaOpenDetailsMode === 'flowSlotAssets'
                                                ? startRowMediaDetailsMode
                                                : 'flowContributors',
                                            );
                                          }
                                        }}
                                      />
                                    );
                                  })()}
                                  <VideoLibrary
                                    color={getFlowMediaColor(flowToUse)}
                                    sx={{ height: 20, width: 20 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        onFlowStartSlotAssetsInlinePress &&
                                        isSmallScreen &&
                                        middleSectionOpensOverlayOnSmallScreen &&
                                        startRowMediaOpenDetailsMode === 'flowSlotAssets'
                                      ) {
                                        onFlowStartSlotAssetsInlinePress();
                                        return;
                                      }
                                      if (onShowFlowDetails) {
                                        setSelectedSlot?.(undefined);
                                        setFlowDetailsDirection?.('up');
                                        onShowFlowDetails(startRowMediaDetailsMode);
                                      }
                                    }}
                                  />
                                </>
                              </Stack>
                            )
                          ) : null}
                        </Stack>
                      ) : startEndItem && isEndItem ? (
                        hideEndItemMediaIndicator && !rightSectionPlayIcon ? (
                          <Stack sx={{ ...rightColumnBoxSx, height: '100%' }} />
                        ) : (
                          <Stack
                            data-cursor-element-id="cursor-el-1"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Control tab: same expand toggle as middle section / session rows.
                              if (flowControlInlineMedia) {
                                handleFlowControlInlineMediaActivate();
                                return;
                              }
                              if (onFlowEndSlotAssetsInlinePress && startRowMediaOpenDetailsMode === 'flowSlotAssets') {
                                onFlowEndSlotAssetsInlinePress();
                                return;
                              }
                              if (!rightSectionOnlyPublishIcon && onShowFlowDetails) {
                                setSelectedSlot?.(undefined);
                                setFlowDetailsDirection?.('down');
                                onShowFlowDetails(startRowMediaDetailsMode);
                              }
                            }}
                            sx={{
                              cursor: rightSectionOnlyPublishIcon ? 'default' : 'pointer',
                              ...(rightSectionPlayIcon ? flowControlRightSectionStackSx : rightSectionStackSx),
                            }}
                          >
                            {flowToUse ? (
                              rightSectionPlayIcon ? (
                                <FlowControlPlayColumnRail onSpacerClick={flowControlPlayIconActivate}>
                                  <SlotRightSectionPlayIcon
                                    segmentAnchor="flow_end"
                                    segmentKind="flow_outro"
                                    onActivate={flowControlPlayIconActivate}
                                    ready={isSegmentPreloaded}
                                    segmentTitle={typeof title === 'string' ? title : undefined}
                                  />
                                </FlowControlPlayColumnRail>
                              ) : (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                  sx={{
                                    p: 1,
                                    width: '100%',
                                    minWidth: 0,
                                    height: '100%',
                                    flexShrink: 0,
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  <VideoLibrary
                                    color={getEndFlowMediaColor(flowToUse)}
                                    sx={{ height: 20, width: 20 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        onFlowEndSlotAssetsInlinePress &&
                                        startRowMediaOpenDetailsMode === 'flowSlotAssets'
                                      ) {
                                        onFlowEndSlotAssetsInlinePress();
                                        return;
                                      }
                                      if (onShowFlowDetails) {
                                        setSelectedSlot?.(undefined);
                                        setFlowDetailsDirection?.('down');
                                        onShowFlowDetails(startRowMediaDetailsMode);
                                      }
                                    }}
                                  />
                                </Stack>
                              )
                            ) : null}
                          </Stack>
                        )
                      ) : startEndItem ? (
                        <Stack sx={{ ...rightColumnBoxSx, height: '100%' }} />
                      ) : null}
                    </>
                  ) : null}
                </Stack>
                {isExpanded && variant === 'normal' && !startEndItem && slot && (
                  <>
                    <Divider orientation="horizontal" />
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        flex: '0 0 32px',
                        height: '32px',
                        minHeight: '32px',
                        maxHeight: '32px',
                        flexShrink: 0,
                      }}
                      data-expanded-form
                    >
                      {expandedFormType === 'timing' && isEditingTiming && slot ? (
                        <>
                          <InlineSlotTimingForm
                            ref={inlineTimingFormRef}
                            slot={slot}
                            gapAfter={gapAfter}
                            isUntimed={isUntimed}
                            onSlotUpdate={onSlotUpdate}
                            onPreviewSlotUpdate={onPreviewSlotUpdate}
                            cancelChanges={cancelChanges}
                            onClose={handleInlineFormClose}
                            onSlotDeleted={onSlotDeleted}
                            localSlots={localSlots}
                            previewFlow={previewFlow}
                            flowEndOffset={flowEndOffset}
                            onShowForm={onShowForm}
                            slotListColumnLayout={slotListColumnLayout}
                            scheduleLiveLockBlocked={scheduleLiveLockBlocked}
                          />
                          {slot && (slot.contentType === 'Session' || slot.contentType === 'Break') ? (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Stack
                                direction="row"
                                data-slot-timing-delete
                                data-cursor-element-id="cursor-el-1"
                                sx={{
                                  cursor: 'pointer',
                                  ...slotListExpandedRightColumnSx(slotListColumnLayout),
                                  touchAction: 'manipulation',
                                }}
                              >
                                {/* Delete/remove session — match InlineSlotEditForm right column (Stack + Button sx) */}
                                <Tooltip
                                  title={
                                    offerSlotRemoval
                                      ? t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
                                      : t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
                                  }
                                  leaveDelay={0}
                                  disableInteractive
                                >
                                  <Button
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      inlineTimingFormRef.current?.openDeleteDialog();
                                    }}
                                    disabled={isOrganizeMode(mode) || scheduleLiveLockBlocked}
                                    sx={{
                                      minWidth: 0,
                                      width: '100%',
                                      ...slotInlineOutlinedActionSx,
                                    }}
                                  >
                                    {offerSlotRemoval ? (
                                      <RemoveCircleOutline fontSize="small" />
                                    ) : (
                                      <DeleteOutlined fontSize="small" />
                                    )}
                                  </Button>
                                </Tooltip>
                              </Stack>
                            </>
                          ) : null}
                        </>
                      ) : expandedFormType === 'new' && isEditingEdit && slot ? (
                        <InlineSlotEditForm
                          key={slot.id}
                          slot={slot}
                          onClose={handleEditFormClose}
                          onSlotDeleted={onSlotDeleted}
                          onExpandedChange={onExpandedChange}
                          onFormTypeChange={onFormTypeChange}
                          onShowTimingAlert={onShowTimingAlert}
                          slotListColumnLayout={slotListColumnLayout}
                          scheduleLiveLockBlocked={scheduleLiveLockBlocked}
                          scheduleInlineTabSwitcher={
                            middleSectionOpensOverlayOnSmallScreen &&
                            smallScreenInlineDetailsVariant === 'schedule' &&
                            slot?.contentType === 'Session' &&
                            inlineScheduleDetailsTab != null &&
                            onInlineScheduleDetailsTabChange
                              ? {
                                  tab: inlineScheduleDetailsTab,
                                  onChange: onInlineScheduleDetailsTabChange,
                                }
                              : undefined
                          }
                        />
                      ) : (
                        <>
                          <SlotListExpandedLeftColumn layout={slotListColumnLayout} />
                          <Divider orientation="vertical" flexItem />
                          <Stack direction="row" alignItems="center" sx={{ p: 1, flex: 1, minWidth: 0 }} />
                          <Divider orientation="vertical" flexItem />
                          {slot && (slot.contentType === 'Session' || slot.contentType === 'Break') ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ ...rightColumnBoxSx, p: 1 }}
                            />
                          ) : null}
                        </>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Box>
    </div>
  );
}
